import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  publicMarketService,
  FALLBACK_LANDING_CONFIG,
  MarketSnapshot,
  OracleCoinRank,
  CoinActivity,
  BtcRegime,
  MicroReversalSnapshot,
  ParticipationSnapshot,
  SectorBreakdownSnapshot,
  SectorActivityGroup,
  MajorCoinsMarketSnapshot,
  OracleFeedSnapshot,
  WinningTradeRow,
  LandingConfig,
} from '../services/publicMarketService';
import { branding } from '../constants/branding';
import { MARKET_SIGNAL_EXPLAINERS } from '../constants/marketSignalExplainers';
import MarketSignalExplainerModal, { SignalCardInfoButton } from '../components/market/MarketSignalExplainerModal';
import MajorCoinsMarketPanel from '../components/market/MajorCoinsMarketPanel';
import CoinPositionsDrawer from '../components/market/CoinPositionsDrawer';
import { store } from '../store';
import { useAppSelector } from '../store/hooks';
import { adminCanOpenMarketSignalPositions } from '../utils/accessControl';

const ADMIN_MARKET_LANDING_PERMS = ['VIEW_MARKET_SIGNALS', 'MANAGE_LANDING_PAGE', 'VIEW_LANDING_PAGE'] as const;

/** Uses Redux store (fresh on each poll) so profile permissions apply after async load. */
function resolvePrivilegedMarketFetch(): 'admin' | 'retail' | null {
  const { auth, profile } = store.getState();
  if (auth.adminUser && auth.token) {
    const a = auth.adminUser;
    if (a.role === 'SUPER_ADMIN' || ADMIN_MARKET_LANDING_PERMS.some((p) => a.permissions?.includes(p))) {
      return 'admin';
    }
  }
  if (auth.isAuthenticated && !auth.adminUser) {
    const perms: string[] = (profile.profile as any)?.permissions ?? [];
    if (perms.includes('VIEW_MARKET_SIGNALS') && (auth.token || auth.type === 'SESSION')) {
      return 'retail';
    }
  }
  return null;
}

// ── viewport meta (ensures 1:1 scaling on mobile) ──────────────────────────

function ensureViewportMeta() {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.name = 'viewport';
    document.head.appendChild(tag);
  }
  tag.content = 'width=device-width, initial-scale=1, maximum-scale=1';
}

// ── responsive hook ─────────────────────────────────────────────────────────

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    ensureViewportMeta();
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

// ── helpers ────────────────────────────────────────────────────────────────

function fmt2(n: number) {
  return n.toFixed(2);
}

function fmtBigNum(n: number) {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function timeAgo(ts: number) {
  const diff = Math.floor(Date.now() / 1000 - ts);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function fmtDateTime(iso?: string | null) {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return 'N/A';
  }
}

const REDACTED_SIGNAL_BLUR: React.CSSProperties = {
  filter: 'blur(5px)',
  userSelect: 'none',
  pointerEvents: 'none',
  opacity: 0.92,
};

// ── Paid Banner types ────────────────────────────────────────────────────────

interface BannerSlot {
  id?:        number;
  side:       'LEFT' | 'RIGHT';
  sortOrder:  number;
  visible:    boolean;
  masked:     boolean;
  badge?:     string | null;
  title?:     string | null;
  subtitle?:  string | null;
  imageUrl?:  string | null;
  linkUrl?:   string | null;
  linkLabel?: string | null;
  bgColor?:   string | null;
  widthPx?:   number | null;
  heightPx?:  number | null;
}

function BannerCard({ b, isMobile }: { b: BannerSlot; isMobile: boolean }) {
  if (!b.visible) return null;

  const bg = b.bgColor || '#f8fafc';
  const hasText = !!(b.badge || b.title || b.subtitle || b.linkUrl);
  // Image-only mode: image fills the entire card with no text block below
  const imageOnly = !!b.imageUrl && !hasText;

  const cardStyle: React.CSSProperties = {
    borderRadius: 16, overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.10)', marginBottom: 16,
    border: '1px solid rgba(0,0,0,0.06)', position: 'relative',
    cursor: b.linkUrl ? 'pointer' : 'default',
    transition: 'transform 0.15s, box-shadow 0.15s',
    ...(b.widthPx  ? { width: b.widthPx } : {}),
    ...(imageOnly
      ? { background: '#000' }                           // pure image card — no bg padding
      : { background: bg, display: 'flex', flexDirection: 'column',
          ...(b.heightPx ? { height: b.heightPx } : {}) }
    ),
  };

  const handleClick = () => { if (b.linkUrl) window.open(b.linkUrl, '_blank', 'noopener'); };

  if (imageOnly) {
    return (
      <div
        style={cardStyle}
        onClick={handleClick}
        onMouseEnter={e => { if (b.linkUrl) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.16)'; }}}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.10)'; }}
      >
        {b.masked && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            backdropFilter: 'blur(8px)', background: 'rgba(255,255,255,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 16,
          }}>
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Sponsored</span>
          </div>
        )}
        <img
          src={b.imageUrl!}
          alt="banner"
          style={{
            width: '100%',
            height: b.heightPx ? b.heightPx : 'auto',
            objectFit: 'cover',
            display: 'block',
          }}
          onError={e => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
        />
      </div>
    );
  }

  // Mixed mode: image on top, text content below
  return (
    <div style={cardStyle} onClick={handleClick}
      onMouseEnter={e => { if (b.linkUrl) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.14)'; }}}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.10)'; }}
    >
      {b.masked && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 10,
          backdropFilter: 'blur(8px)', background: 'rgba(255,255,255,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 16,
        }}>
          <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Sponsored</span>
        </div>
      )}
      {b.imageUrl && (
        <img
          src={b.imageUrl}
          alt={b.title || 'banner'}
          style={{ width: '100%', height: b.heightPx ? b.heightPx * 0.52 : 150, objectFit: 'cover', display: 'block' }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {b.badge && (
          <span style={{
            display: 'inline-block', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            color: '#086afb', background: 'rgba(8,106,251,0.08)',
            borderRadius: 20, padding: '2px 9px', alignSelf: 'flex-start',
          }}>{b.badge}</span>
        )}
        {b.title && (
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', lineHeight: 1.35 }}>
            {b.title}
          </div>
        )}
        {b.subtitle && (
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.55 }}>
            {b.subtitle}
          </div>
        )}
        {b.linkUrl && (
          <div style={{ marginTop: 8 }}>
            <span style={{
              display: 'inline-block', fontSize: 12, fontWeight: 700,
              color: '#fff',
              background: 'linear-gradient(135deg, #086afb, #e7038e)',
              borderRadius: 20, padding: '5px 16px',
            }}>
              {b.linkLabel || 'Learn More'} →
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function readBanners(snapshot: any, side: 'LEFT' | 'RIGHT'): BannerSlot[] {
  const list: any[] = snapshot?.banners ?? [];
  return list
    .filter((b: any) => b.side === side && b.visible)
    .sort((a: any, b: any) => a.sortOrder - b.sortOrder);
}

/** All visible banners in display order (LEFT first, then RIGHT). */
function readAllBanners(snapshot: any): BannerSlot[] {
  const list: any[] = snapshot?.banners ?? [];
  return list
    .filter((b: any) => b.visible)
    .sort((a: any, b: any) => {
      if (a.side !== b.side) return a.side === 'LEFT' ? -1 : 1;
      return a.sortOrder - b.sortOrder;
    });
}

function MobileBannerStrip({ banners }: { banners: BannerSlot[] }) {
  if (banners.length === 0) return null;
  return (
    <div style={{
      overflowX: 'auto',
      display: 'flex',
      gap: 12,
      padding: '4px 16px 12px',   /* 16px each side so first/last cards align with page content */
      WebkitOverflowScrolling: 'touch',
      msOverflowStyle: 'none',
      scrollbarWidth: 'none' as const,
    }}>
      {banners.map(b => {
        const bg = b.bgColor || '#f8fafc';
        return (
          <div key={b.id} style={{
            minWidth: 220, maxWidth: 260, flex: '0 0 auto',
            background: bg, borderRadius: 14, overflow: 'hidden',
            boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
            border: '1px solid rgba(0,0,0,0.06)', position: 'relative',
          }}>
            {b.masked && (
              <div style={{
                position: 'absolute', inset: 0, zIndex: 10,
                backdropFilter: 'blur(6px)', background: 'rgba(255,255,255,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 14,
              }}>
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Sponsored</span>
              </div>
            )}
            {b.imageUrl && (
              <img src={b.imageUrl} alt={b.title || 'banner'}
                style={{ width: '100%', maxHeight: 110, objectFit: 'cover', display: 'block' }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <div style={{ padding: '12px 14px' }}>
              {b.badge && (
                <span style={{
                  display: 'inline-block', fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: '#6366f1', background: 'rgba(99,102,241,0.1)',
                  borderRadius: 20, padding: '2px 7px', marginBottom: 5,
                }}>{b.badge}</span>
              )}
              {b.title && (
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 3, lineHeight: 1.3 }}>
                  {b.title}
                </div>
              )}
              {b.subtitle && (
                <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5, marginBottom: b.linkUrl ? 8 : 0 }}>
                  {b.subtitle}
                </div>
              )}
              {b.linkUrl && (
                <a href={b.linkUrl} target="_blank" rel="noopener noreferrer"
                  style={{
                    display: 'inline-block', fontSize: 11, fontWeight: 600,
                    color: '#fff', background: '#6366f1', borderRadius: 20,
                    padding: '4px 12px', textDecoration: 'none',
                  }}>
                  {b.linkLabel || 'Learn More'}
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Public “mask all” mode: keep layout full; values are dummy + blurred (no API leak). */
function buildMaskedBtcRegime(): BtcRegime {
  const ts = Math.floor(Date.now() / 1000) - 120;
  return {
    regime: 'RANGING',
    description: '▓▓▓▓ ▓▓▓▓▓▓ ▓▓▓ ▓▓▓▓▓ ▓▓▓▓',
    colour: 'grey',
    price: 82341.5,
    change24h: 0.37,
    updatedAt: ts,
  };
}

function buildMaskedMicroReversal(): MicroReversalSnapshot {
  const ts = Math.floor(Date.now() / 1000) - 120;
  return {
    blocked: false,
    status: 'UNKNOWN',
    description: '▓▓▓▓ ▓▓▓▓▓▓ ▓▓▓ ▓▓▓▓▓ ▓▓▓▓',
    colour: 'grey',
    dropFromHighPercent: -0.04,
    localHigh: 82410,
    currentPrice: 82341.5,
    minDropPercent: -0.5,
    maxDropPercent: -0.1,
    lookbackCandles: 12,
    updatedAt: ts,
    available: false,
  };
}

function buildMaskedParticipation(): ParticipationSnapshot {
  const ts = Math.floor(Date.now() / 1000) - 120;
  const coins: CoinActivity[] = Array.from({ length: 12 }, (_, i) => ({
    symbol: `▓${(i + 1).toString().padStart(2, '0')}`.padEnd(6, '▓'),
    change24h: ((i * 7) % 5) - 2,
    volume24h: 1_200_000 + i * 413_000,
    active: i % 4 !== 0,
  }));
  return {
    level: 'MODERATE',
    description: '▓▓▓ ▓▓▓▓▓ ▓▓▓▓ ▓▓▓▓▓▓ ▓▓▓ ▓▓▓▓',
    rate: 0.48,
    activeCoins: 5,
    totalCoins: 12,
    coins,
    updatedAt: ts,
  };
}

function buildMaskedSectorBreakdown(): SectorBreakdownSnapshot {
  const ts = Math.floor(Date.now() / 1000) - 120;
  const labels = ['Layer 1', 'DeFi', 'Gaming', 'NFT', 'Meme', 'Stock Index'];
  const sectors: SectorActivityGroup[] = labels.map((label, si) => ({
    id: `MASK_${si}`,
    label,
    activeCoins: 2,
    totalCoins: 4,
    rate: 0.5,
    coins: Array.from({ length: 4 }, (_, i) => ({
      symbol: `▓${(si + 1).toString()}${i}`.padEnd(5, '▓').slice(0, 5),
      change24h: ((i + si) % 5) - 2,
      volume24h: 900_000 + i * 210_000,
      active: i % 2 === 0,
    })),
  }));
  return { sectors, updatedAt: ts, available: false };
}

function buildMaskedFuturesActivity(): SectorActivityGroup {
  const stockSymbols = ['SOXL', 'DRAM', 'MU', 'MSTR', 'SNDK', 'SPCX'];
  return {
    id: 'FUTURES',
    label: 'Stock Index',
    activeCoins: 3,
    totalCoins: stockSymbols.length,
    rate: 0.5,
    coins: stockSymbols.map((symbol, i) => ({
      symbol,
      change24h: ((i * 3) % 5) - 2,
      volume24h: 4_800_000 + i * 920_000,
      active: i % 2 === 0,
    })),
  };
}

const CORE_SECTOR_IDS = ['LAYER_1', 'DEFI', 'GAMING', 'NFT', 'MEME'] as const;
const SECTOR_BREAKDOWN_MIN_CORE_SECTORS = 4;
const SECTOR_BREAKDOWN_MIN_TOTAL_COINS = 80;

function sectorBreakdownTotalCoins(value: SectorBreakdownSnapshot | null | undefined): number {
  return value?.sectors?.reduce((n, s) => n + (s.coins?.length ?? 0), 0) ?? 0;
}

function sectorBreakdownCoreSectorCount(value: SectorBreakdownSnapshot | null | undefined): number {
  return value?.sectors?.filter(s => CORE_SECTOR_IDS.includes(s.id as typeof CORE_SECTOR_IDS[number])).length ?? 0;
}

function sectorBreakdownRichness(value: SectorBreakdownSnapshot | null | undefined): number {
  return sectorBreakdownCoreSectorCount(value) * 10_000 + sectorBreakdownTotalCoins(value);
}

function isSectorBreakdownHealthy(value: SectorBreakdownSnapshot | null | undefined): boolean {
  if (!value?.sectors?.length) return false;
  return sectorBreakdownCoreSectorCount(value) >= SECTOR_BREAKDOWN_MIN_CORE_SECTORS
    && sectorBreakdownTotalCoins(value) >= SECTOR_BREAKDOWN_MIN_TOTAL_COINS;
}

function sectorBreakdownHasCoins(value: SectorBreakdownSnapshot | null | undefined): boolean {
  return isSectorBreakdownHealthy(value);
}

function shouldKeepPreviousSectorBreakdown(
  prev: SectorBreakdownSnapshot | undefined,
  incoming: SectorBreakdownSnapshot | undefined,
): boolean {
  if (!incoming?.sectors?.length) return isSectorBreakdownHealthy(prev);
  if (!isSectorBreakdownHealthy(prev)) return false;
  return sectorBreakdownRichness(incoming) < sectorBreakdownRichness(prev);
}

function futuresActivityHasCoins(value: SectorActivityGroup | null | undefined): boolean {
  return (value?.coins?.length ?? 0) > 0;
}

type LandingMarketSnapshot = MarketSnapshot & Partial<LandingConfig> & { signalViewer?: boolean };

function mergeLandingMarketSnapshot(
  prev: LandingMarketSnapshot | null | undefined,
  data: LandingMarketSnapshot,
): LandingMarketSnapshot {
  const next: LandingMarketSnapshot = { ...(prev ?? {}), ...data };

  const incomingSector = data.sectorBreakdown;
  const prevSector = prev?.sectorBreakdown;
  if (shouldKeepPreviousSectorBreakdown(prevSector, incomingSector)) {
    next.sectorBreakdown = prevSector;
  }

  const incomingFutures = data.futuresActivity;
  const prevFutures = prev?.futuresActivity;
  if (!futuresActivityHasCoins(incomingFutures) && futuresActivityHasCoins(prevFutures)) {
    next.futuresActivity = prevFutures;
  }

  if (data.maskAllSections === true && !data.signalViewer) {
    if (data.maskBtcRegime !== false) {
      delete next.btcRegime;
    }
    if (data.maskMicroReversal !== false) {
      delete next.microReversal;
    }
    if (data.maskParticipation !== false) {
      delete next.participation;
    }
    if (data.maskMajorCoinsMarket !== false) {
      delete next.majorCoinsMarket;
    }
    delete next.oracleFeed;
    delete next.oracleFeedStockIndex;
    if (data.maskWinningTrades !== false) {
      delete next.winningTrades;
    }
  }

  return next;
}

function buildMaskedMajorCoinsMarket(): MajorCoinsMarketSnapshot {
  const ts = Math.floor(Date.now() / 1000) - 120;
  const symbols = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'AVAX', 'DOT', 'LINK', 'MATIC'];
  const coins = symbols.map((symbol, i) => ({
    symbol,
    name: '▓▓▓▓',
    price: 42000 - i * 1200,
    change24h: ((i % 5) - 2) * 0.4,
    marketCap: 900_000_000_000 - i * 45_000_000_000,
    sharePercent: Math.max(2, 48 - i * 4.5),
  }));
  return {
    coins,
    totalMarketCap: coins.reduce((s, c) => s + c.marketCap, 0),
    updatedAt: ts,
    source: 'unavailable',
  };
}

function buildMaskedOracleFeed(rowCount: number): OracleFeedSnapshot {
  const ts = Math.floor(Date.now() / 1000) - 120;
  const n = Math.min(10, Math.max(4, rowCount));
  const coins: OracleCoinRank[] = Array.from({ length: n }, (_, i) => ({
    symbol: `▓▓X${i}`,
    signal: i % 4 === 0 ? 'LONG' : i % 4 === 1 ? 'SHORT' : 'WATCH',
    score: 0.25 + (i % 5) * 0.11,
    reasons: ['▓▓▓▓▓▓', '▓▓▓▓'],
    rank: i + 1,
    tier: 'neutral',
  }));
  return { oracleActive: false, confidence: 0.42, coins, updatedAt: ts };
}

function buildMaskedWinningTrades(count = 20): WinningTradeRow[] {
  return Array.from({ length: count }, (_, i) => ({
    symbol: `████${i + 1}`,
    side: i % 2 === 0 ? 'LONG' : 'SHORT',
    entryPrice: 0.00071 + i * 0.000003,
    exitPrice: 0.00082 + i * 0.000003,
    pnl: i % 3 === 0 ? -(1.2 + i * 0.11) : 2.1 + i * 0.17,
    dailyPnl: i % 4 === 0 ? -(3.5 + i * 0.2) : 4.2 + i * 0.31,
    dailyPnlPerStrategy: i % 3 === 0 ? -(1.1 + i * 0.15) : 1.8 + i * 0.22,
    exitReason: '██████████',
    closedAt: new Date(Date.now() - (i + 1) * 3600_000).toISOString(),
    strategyName: '████ ████',
  }));
}

function fmtPnl(value: number, redact?: boolean) {
  if (redact) return value >= 0 ? '+$██.██' : '-$██.██';
  const abs = Math.abs(value);
  const body = abs < 0.01 && abs !== 0 ? abs.toFixed(6) : abs.toFixed(2);
  return `${value >= 0 ? '+' : '-'}$${body}`;
}

function pnlColor(value: number) {
  if (value > 0) return '#15803d';
  if (value < 0) return '#b91c1c';
  return '#475569';
}

// ── colour maps ────────────────────────────────────────────────────────────

const REGIME_COLOURS = {
  green: { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46', dot: '#10b981' },
  red:   { bg: '#fee2e2', border: '#fca5a5', text: '#7f1d1d', dot: '#ef4444' },
  amber: { bg: '#fef3c7', border: '#fde68a', text: '#78350f', dot: '#f59e0b' },
  grey:  { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569', dot: '#94a3b8' },
};

const SIGNAL_COLOURS: Record<string, { bg: string; text: string; label: string }> = {
  LONG:    { bg: '#d1fae5', text: '#065f46', label: 'LONG ↑' },
  SHORT:   { bg: '#fee2e2', text: '#7f1d1d', label: 'SHORT ↓' },
  WATCH:   { bg: '#eff6ff', text: '#1e40af', label: 'WATCH' },
  NEUTRAL: { bg: '#f1f5f9', text: '#475569', label: 'NEUTRAL' },
};

const PARTICIPATION_COLOURS = {
  HIGH:     { bg: '#d1fae5', text: '#065f46', dot: '#10b981', label: 'HIGH ACTIVITY' },
  MODERATE: { bg: '#fef3c7', text: '#78350f', dot: '#f59e0b', label: 'MODERATE' },
  LOW:      { bg: '#fee2e2', text: '#7f1d1d', dot: '#ef4444', label: 'LOW ACTIVITY' },
  UNKNOWN:  { bg: '#f1f5f9', text: '#475569', dot: '#94a3b8', label: 'LOADING…' },
};

const AI_REGIME_COLOURS: Record<string, { bg: string; border: string; text: string; dot: string; icon: string; desc: string }> = {
  HOT_MEME:        { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46', dot: '#10b981', icon: '🔥', desc: 'High coin activity — optimal trading conditions' },
  HIGH_VOLATILITY: { bg: '#ede9fe', border: '#c4b5fd', text: '#4c1d95', dot: '#7c3aed', icon: '⚡', desc: 'Extreme volatility — tight exits in effect' },
  LOW_PARTICIPATION:{ bg: '#fee2e2', border: '#fca5a5', text: '#7f1d1d', dot: '#ef4444', icon: '😴', desc: 'Few coins moving — reduced expectancy' },
  BEARISH_BTC:     { bg: '#fef3c7', border: '#fde68a', text: '#78350f', dot: '#f59e0b', icon: '🐻', desc: 'BTC downtrend — LONG entries at risk' },
  MODERATE:        { bg: '#f0f9ff', border: '#bae6fd', text: '#0c4a6e', dot: '#0284c7', icon: '⚖️', desc: 'Balanced market — default parameters apply' },
  UNKNOWN:         { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569', dot: '#94a3b8', icon: '❓', desc: 'Regime data loading…' },
};

// ── sub-components ─────────────────────────────────────────────────────────

interface AiRegimeBadgeProps { data: { regime: string; label: string; updatedAt: number }; masked?: boolean }
const AiRegimeBadge: React.FC<AiRegimeBadgeProps> = ({ data, masked }) => {
  const [explainerOpen, setExplainerOpen] = useState(false);
  const c = AI_REGIME_COLOURS[data.regime] ?? AI_REGIME_COLOURS.UNKNOWN;
  return (
    <>
      <div style={{
        background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 16,
        padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: c.text, letterSpacing: 1, textTransform: 'uppercase' }}>
            AI Market Regime
          </span>
          <SignalCardInfoButton
            onClick={() => setExplainerOpen(true)}
            color={c.text}
            ariaLabel="Explain AI Market Regime"
          />
        </div>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8,
          filter: masked ? 'blur(6px)' : undefined,
          userSelect: masked ? 'none' : undefined,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>{c.icon}</span>
            <span style={{ fontSize: 20, fontWeight: 800, color: c.text, textTransform: 'uppercase' }}>{data.label}</span>
          </div>
          <div style={{ fontSize: 13, color: c.text, opacity: 0.85 }}>{c.desc}</div>
          <div style={{ fontSize: 11, color: c.text, opacity: 0.6, marginTop: 2 }}>
            Combined signal · Updated {timeAgo(data.updatedAt)}
          </div>
        </div>
      </div>
      <MarketSignalExplainerModal
        open={explainerOpen}
        onClose={() => setExplainerOpen(false)}
        content={MARKET_SIGNAL_EXPLAINERS.ai_regime}
        masked={masked}
        currentLabel={data.label}
        currentDetail={c.desc}
      />
    </>
  );
};

interface BtcRegimeBadgeProps { regime: BtcRegime; masked?: boolean }
const BtcRegimeBadge: React.FC<BtcRegimeBadgeProps> = ({ regime, masked }) => {
  const [explainerOpen, setExplainerOpen] = useState(false);
  const c = REGIME_COLOURS[regime.colour] ?? REGIME_COLOURS.grey;
  return (
    <>
      <div style={{
        background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 16,
        padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: c.text, letterSpacing: 1, textTransform: 'uppercase' }}>
            BTC Market Regime
          </span>
          <SignalCardInfoButton
            onClick={() => setExplainerOpen(true)}
            color={c.text}
            ariaLabel="Explain BTC Market Regime"
          />
        </div>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8,
          filter: masked ? 'blur(6px)' : undefined,
          userSelect: masked ? 'none' : undefined,
        }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: c.text }}>{regime.regime}</div>
          <div style={{ fontSize: 13, color: c.text, opacity: 0.85 }}>{regime.description}</div>
          <div style={{ display: 'flex', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: c.text }}>
              Price: <strong>${regime.price.toLocaleString()}</strong>
            </span>
            <span style={{ fontSize: 13, color: regime.change24h >= 0 ? '#065f46' : '#7f1d1d' }}>
              24h: <strong>{regime.change24h >= 0 ? '+' : ''}{fmt2(regime.change24h)}%</strong>
            </span>
          </div>
          <div style={{ fontSize: 11, color: c.text, opacity: 0.6, marginTop: 2 }}>
            Updated {timeAgo(regime.updatedAt)}
          </div>
        </div>
      </div>
      <MarketSignalExplainerModal
        open={explainerOpen}
        onClose={() => setExplainerOpen(false)}
        content={MARKET_SIGNAL_EXPLAINERS.btc_regime}
        masked={masked}
        currentLabel={regime.regime}
        currentDetail={regime.description}
      />
    </>
  );
};

interface MicroReversalBadgeProps { data: MicroReversalSnapshot }
const MicroReversalBadge: React.FC<MicroReversalBadgeProps> = ({ data }) => {
  const c = REGIME_COLOURS[data.colour] ?? REGIME_COLOURS.grey;
  const statusLabel = data.blocked ? 'SIGNALS BLOCKED' : data.status === 'CLEAR' ? 'CLEAR' : 'MONITORING';
  return (
    <div style={{
      background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 16,
      padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: c.text, letterSpacing: 1, textTransform: 'uppercase' }}>
          Micro-Reversal Filter
        </span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: c.text }}>{statusLabel}</div>
      <div style={{ fontSize: 13, color: c.text, opacity: 0.85 }}>{data.description}</div>
      <div style={{ display: 'flex', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: c.text }}>
          Drop from high: <strong>{fmt2(data.dropFromHighPercent)}%</strong>
        </span>
        <span style={{ fontSize: 13, color: c.text }}>
          BTC: <strong>${data.currentPrice.toLocaleString()}</strong>
        </span>
      </div>
      <div style={{ fontSize: 11, color: c.text, opacity: 0.6, marginTop: 2 }}>
        5m lookback · Updated {timeAgo(data.updatedAt)}
      </div>
    </div>
  );
};

interface ParticipationBadgeProps { data: ParticipationSnapshot; masked?: boolean }
const ParticipationBadge: React.FC<ParticipationBadgeProps> = ({ data, masked }) => {
  const [explainerOpen, setExplainerOpen] = useState(false);
  const c = PARTICIPATION_COLOURS[data.level] ?? PARTICIPATION_COLOURS.UNKNOWN;
  const pct = Math.round(data.rate * 100);
  return (
    <>
      <div style={{
        background: c.bg, border: `1.5px solid`, borderColor: c.dot + '44',
        borderRadius: 16, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: c.text, letterSpacing: 1, textTransform: 'uppercase' }}>
            Meme Participation
          </span>
          <SignalCardInfoButton
            onClick={() => setExplainerOpen(true)}
            color={c.text}
            ariaLabel="Explain Meme Participation"
          />
        </div>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8,
          filter: masked ? 'blur(6px)' : undefined,
          userSelect: masked ? 'none' : undefined,
        }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: c.text }}>{c.label}</div>
          <div style={{ fontSize: 13, color: c.text, opacity: 0.85 }}>{data.description}</div>
          <div style={{ height: 8, background: c.dot + '33', borderRadius: 4, overflow: 'hidden', marginTop: 4 }}>
            <div style={{ height: '100%', width: `${pct}%`, background: c.dot, borderRadius: 4, transition: 'width 0.8s ease' }} />
          </div>
          <div style={{ fontSize: 11, color: c.text, opacity: 0.6, marginTop: 2 }}>
            {data.activeCoins}/{data.totalCoins} coins active · Updated {timeAgo(data.updatedAt)}
          </div>
        </div>
      </div>
      <MarketSignalExplainerModal
        open={explainerOpen}
        onClose={() => setExplainerOpen(false)}
        content={MARKET_SIGNAL_EXPLAINERS.participation}
        masked={masked}
        currentLabel={c.label}
        currentDetail={data.description}
      />
    </>
  );
};

// ── Oracle feed — mobile-aware ───────────────────────────────────────────────

const ORACLE_UNIVERSE_KEY = 'oracleFeedUniverse';
type OracleUniverse = 'MEME' | 'STOCK_INDEX';

interface OracleFeedTableProps {
  memeFeed: OracleFeedSnapshot;
  stockFeed?: OracleFeedSnapshot | null;
  limit?: number;
}

const OracleFeedTable: React.FC<OracleFeedTableProps> = ({ memeFeed, stockFeed, limit = 10 }) => {
  const isMobile = useIsMobile();
  const [hovered, setHovered] = useState<number | null>(null);
  const [universe, setUniverse] = useState<OracleUniverse>(() => {
    try {
      const saved = localStorage.getItem(ORACLE_UNIVERSE_KEY);
      if (saved === 'MEME' || saved === 'STOCK_INDEX') return saved;
    } catch { /* ignore */ }
    return 'MEME';
  });

  const selectUniverse = (next: OracleUniverse) => {
    setUniverse(next);
    try { localStorage.setItem(ORACLE_UNIVERSE_KEY, next); } catch { /* ignore */ }
  };

  const feed = universe === 'STOCK_INDEX' ? (stockFeed ?? memeFeed) : memeFeed;
  const coins = feed.coins.slice(0, limit);
  const universeLabel = universe === 'STOCK_INDEX' ? 'Stock Index' : 'Memes';

  const universeBtn = (mode: OracleUniverse, label: string) => {
    const active = universe === mode;
    return (
      <button
        type="button"
        onClick={() => selectUniverse(mode)}
        style={{
          padding: '5px 12px',
          borderRadius: 999,
          border: `1px solid ${active ? '#086afb' : '#e2e8f0'}`,
          background: active ? '#086afb' : '#fff',
          color: active ? '#fff' : '#64748b',
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>
      {/* header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: feed.oracleActive ? '#10b981' : '#94a3b8', flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
            Oracle Signal Feed
          </span>
          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
            · {universeLabel}
          </span>
          {feed.oracleActive && (
            <span style={{ background: '#eff6ff', color: '#1e40af', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
              CONFIDENCE {Math.round(feed.confidence * 100)}%
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {universeBtn('MEME', 'Memes')}
            {universeBtn('STOCK_INDEX', 'Stock Index')}
          </div>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>Updated {timeAgo(feed.updatedAt)}</span>
        </div>
      </div>

      {universe === 'STOCK_INDEX' && !stockFeed && (
        <div style={{ padding: '10px 16px', background: '#fffbeb', borderBottom: '1px solid #fde68a', fontSize: 12, color: '#92400e' }}>
          Stock-index rankings are loading. Showing meme rankings until available.
        </div>
      )}

      {coins.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          Oracle data loading…
        </div>
      ) : isMobile ? (
        /* ── mobile: card list instead of table ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {coins.map((coin) => {
            const sc = SIGNAL_COLOURS[coin.signal] ?? SIGNAL_COLOURS.NEUTRAL;
            const isHot = coin.tier === 'oracle-top';
            return (
              <div key={coin.symbol} style={{
                padding: '12px 16px',
                borderBottom: '1px solid #f8fafc',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 12, color: '#94a3b8', width: 18, flexShrink: 0 }}>{coin.rank}</span>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: isHot ? branding.primaryColor : '#e2e8f0',
                  color: isHot ? '#fff' : '#475569',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 11,
                }}>
                  {coin.symbol.slice(0, 3)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{coin.symbol}</span>
                    {isHot && <span style={{ fontSize: 9, background: '#fef3c7', color: '#92400e', padding: '1px 5px', borderRadius: 8, fontWeight: 700 }}>TOP</span>}
                    <span style={{ background: sc.bg, color: sc.text, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, marginLeft: 'auto' }}>
                      {sc.label}
                    </span>
                  </div>
                  {coin.reasons.length > 0 && (
                    <div style={{ fontSize: 11, color: '#64748b' }}>{coin.reasons[0]}</div>
                  )}
                </div>
                {coin.score > 0 && (
                  <div style={{ flexShrink: 0, textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{Math.round(coin.score * 100)}</div>
                    <div style={{ width: 40, height: 4, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden', marginTop: 3 }}>
                      <div style={{ height: '100%', width: `${Math.round(coin.score * 100)}%`, background: branding.primaryColor, borderRadius: 2 }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ── desktop: full table ── */
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 500 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Coin</th>
                <th style={thStyle}>Signal</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Score</th>
                <th style={thStyle}>Reasons</th>
              </tr>
            </thead>
            <tbody>
              {coins.map((coin, idx) => {
                const sc = SIGNAL_COLOURS[coin.signal] ?? SIGNAL_COLOURS.NEUTRAL;
                const isHot = coin.tier === 'oracle-top';
                return (
                  <tr key={coin.symbol}
                    onMouseEnter={() => setHovered(idx)}
                    onMouseLeave={() => setHovered(null)}
                    style={{ background: hovered === idx ? '#f8fafc' : '#fff', transition: 'background 0.15s' }}>
                    <td style={{ ...tdStyle, color: '#94a3b8', width: 36 }}>{coin.rank}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: isHot ? branding.primaryColor : '#e2e8f0',
                          color: isHot ? '#fff' : '#475569',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 700, fontSize: 11, flexShrink: 0,
                        }}>
                          {coin.symbol.slice(0, 3)}
                        </div>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{coin.symbol}</span>
                        {isHot && <span style={{ fontSize: 9, background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>TOP</span>}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ background: sc.bg, color: sc.text, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>
                        {sc.label}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {coin.score > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                          <div style={{ width: 60, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.round(coin.score * 100)}%`, background: branding.primaryColor, borderRadius: 3 }} />
                          </div>
                          <span style={{ color: '#1e293b', fontWeight: 600, minWidth: 36 }}>
                            {Math.round(coin.score * 100)}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 260 }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {coin.reasons.slice(0, 2).map((r, i) => (
                          <span key={i} style={{ fontSize: 11, background: '#f1f5f9', color: '#475569', padding: '2px 7px', borderRadius: 8 }}>
                            {r}
                          </span>
                        ))}
                        {coin.reasons.length > 2 && (
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>+{coin.reasons.length - 2}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px', textAlign: 'left', fontSize: 11,
  fontWeight: 700, color: '#94a3b8', letterSpacing: 0.5, textTransform: 'uppercase',
};
const tdStyle: React.CSSProperties = {
  padding: '12px 16px', borderBottom: '1px solid #f8fafc', verticalAlign: 'middle',
};

// ── participation coin grid ─────────────────────────────────────────────────

const COIN_ACTIVITY_VIEW_KEY = 'coinActivityViewMode';

interface CoinGridProps {
  coins: CoinActivity[];
  interactive?: boolean;
  onCoinClick?: (coin: CoinActivity) => void;
}
const CoinGrid: React.FC<CoinGridProps> = ({ coins, interactive, onCoinClick }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginTop: 12 }}>
    {coins.map(c => {
      const cardStyle: React.CSSProperties = {
        background: c.active ? '#f0fdf4' : '#fef2f2',
        border: `1px solid ${c.active ? '#bbf7d0' : '#fecaca'}`,
        borderRadius: 10,
        padding: '10px 12px',
        ...(interactive ? { cursor: 'pointer', transition: 'box-shadow 0.15s ease' } : {}),
      };
      const inner = (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: '#1e293b' }}>{c.symbol}</span>
            <span style={{ fontSize: 9, fontWeight: 700, background: c.active ? '#dcfce7' : '#fee2e2', color: c.active ? '#15803d' : '#b91c1c', padding: '1px 5px', borderRadius: 6 }}>
              {c.active ? 'HOT' : 'COOL'}
            </span>
          </div>
          <div style={{ fontSize: 12, marginTop: 4, color: c.change24h >= 0 ? '#15803d' : '#b91c1c', fontWeight: 600 }}>
            {c.change24h >= 0 ? '+' : ''}{fmt2(c.change24h)}%
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmtBigNum(c.volume24h)} vol</div>
          {interactive && (
            <div style={{ fontSize: 10, color: '#086afb', marginTop: 6, fontWeight: 600 }}>Trade / positions →</div>
          )}
        </>
      );
      if (interactive && onCoinClick) {
        return (
          <button
            key={c.symbol}
            type="button"
            onClick={() => onCoinClick(c)}
            style={{
              ...cardStyle,
              textAlign: 'left',
              width: '100%',
              font: 'inherit',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(8,106,251,0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
          >
            {inner}
          </button>
        );
      }
      return (
        <div key={c.symbol} style={cardStyle}>
          {inner}
        </div>
      );
    })}
  </div>
);

interface SectorCoinGridProps {
  sectors: SectorActivityGroup[];
  isMobile: boolean;
  masked?: boolean;
  interactive?: boolean;
  onCoinClick?: (coin: CoinActivity) => void;
}
const SectorCoinGrid: React.FC<SectorCoinGridProps> = ({ sectors, isMobile, masked, interactive, onCoinClick }) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [sectorSearch, setSectorSearch] = useState('');

  const toggleSector = (id: string) => {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const q = sectorSearch.trim().toLowerCase();
  const totalCoins = sectors.reduce((n, s) => n + s.coins.length, 0);
  const visibleSectors = sectors
    .map(sector => ({
      sector,
      filteredCoins: q
        ? sector.coins.filter(c => c.symbol.toLowerCase().includes(q))
        : sector.coins,
    }))
    .filter(({ filteredCoins }) => !q || filteredCoins.length > 0);

  const matchCount = visibleSectors.reduce((n, { filteredCoins }) => n + filteredCoins.length, 0);

  return (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {sectors.length > 0 && (
        <div style={{ marginBottom: 2 }}>
          <input
            type="search"
            value={sectorSearch}
            onChange={e => setSectorSearch(e.target.value)}
            placeholder="Search all sectors (e.g. BTC, PEPE, SOXL, LINK)…"
            aria-label="Search coins across all sectors"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
            {q
              ? `Showing ${matchCount} match${matchCount !== 1 ? 'es' : ''} across ${visibleSectors.length} sector${visibleSectors.length !== 1 ? 's' : ''}`
              : `${totalCoins} coins across ${sectors.length} sectors`}
          </div>
        </div>
      )}

      {q && visibleSectors.length === 0 && (
        <p style={{ margin: '8px 0', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
          No coins match &ldquo;{sectorSearch.trim()}&rdquo; in any sector.
        </p>
      )}

      {visibleSectors.map(({ sector, filteredCoins }) => {
        const pct = Math.round(sector.rate * 100);
        const isCollapsed = collapsed[sector.id] === true;
        const isLargeSector = sector.coins.length > 20;

        return (
          <div key={sector.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => isMobile && toggleSector(sector.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '12px 14px',
                background: '#f8fafc',
                border: 'none',
                cursor: isMobile ? 'pointer' : 'default',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                {isMobile && (
                  <span style={{ fontSize: 11, color: '#64748b', flexShrink: 0 }}>
                    {isCollapsed ? '▶' : '▼'}
                  </span>
                )}
                <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {sector.label}
                </span>
              </div>
              <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {q
                  ? `${filteredCoins.length}/${sector.coins.length} shown · `
                  : ''}
                {sector.activeCoins}/{sector.totalCoins} active · {pct}% HOT
              </span>
            </button>
            {(!isMobile || !isCollapsed) && (
              <div style={{
                padding: '0 12px 12px',
                filter: masked ? 'blur(6px)' : undefined,
                userSelect: masked ? 'none' : undefined,
              }}>
                <div style={isLargeSector ? { maxHeight: 520, overflowY: 'auto', paddingTop: 10, paddingRight: 2 } : { paddingTop: 10 }}>
                  <CoinGrid coins={filteredCoins} interactive={interactive} onCoinClick={onCoinClick} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

type CoinActivityViewMode = 'flat' | 'sector';

interface CoinActivityBreakdownPanelProps {
  participation: ParticipationSnapshot;
  sectorBreakdown?: SectorBreakdownSnapshot;
  futuresActivity?: SectorActivityGroup;
  showSectorTab: boolean;
  isMobile: boolean;
  masked?: boolean;
  enableCoinActions?: boolean;
  onCoinClick?: (coin: CoinActivity) => void;
}
const CoinActivityBreakdownPanel: React.FC<CoinActivityBreakdownPanelProps> = ({
  participation,
  sectorBreakdown,
  futuresActivity,
  showSectorTab,
  isMobile,
  masked,
  enableCoinActions,
  onCoinClick,
}) => {
  const sectorReady = showSectorTab && (
    isSectorBreakdownHealthy(sectorBreakdown)
    || (masked === true && (sectorBreakdown?.sectors?.length ?? 0) > 0)
  );

  const [viewMode, setViewMode] = useState<CoinActivityViewMode>(() => {
    try {
      const saved = localStorage.getItem(COIN_ACTIVITY_VIEW_KEY);
      if (saved === 'sector' || saved === 'flat') return saved;
    } catch { /* ignore */ }
    return 'flat';
  });

  useEffect(() => {
    if (!sectorReady) return;
    try {
      const saved = localStorage.getItem(COIN_ACTIVITY_VIEW_KEY);
      if (saved === 'sector' || saved === 'flat') {
        setViewMode(saved);
      }
    } catch { /* ignore */ }
  }, [sectorReady, sectorBreakdown?.updatedAt]);

  const futuresSector = futuresActivity
    ?? sectorBreakdown?.sectors?.find(s => s.id === 'FUTURES');
  const showStandaloneFutures = viewMode !== 'sector' && !!futuresSector && futuresSector.coins.length > 0;
  const sectorViewActive = viewMode === 'sector';
  const sectorDataReady = sectorViewActive && sectorReady && !!sectorBreakdown;

  const selectView = (mode: CoinActivityViewMode) => {
    setViewMode(mode);
    try { localStorage.setItem(COIN_ACTIVITY_VIEW_KEY, mode); } catch { /* ignore */ }
  };

  const tabBtn = (mode: CoinActivityViewMode, label: string) => {
    const active = viewMode === mode;
    return (
      <button
        type="button"
        onClick={() => selectView(mode)}
        style={{
          padding: '6px 14px',
          borderRadius: 999,
          border: `1px solid ${active ? '#086afb' : '#e2e8f0'}`,
          background: active ? '#086afb' : '#fff',
          color: active ? '#fff' : '#64748b',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 16 : 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 4 }}>
            Coin Activity Breakdown
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>
            {sectorViewActive && sectorDataReady
              ? 'Green = positive price movement + above-median volume within each sector · use search to filter all sectors'
              : sectorViewActive
                ? 'Loading industry sector breakdown…'
              : showStandaloneFutures
                ? 'Meme participation above; all KuCoin stock-index perpetuals below (searchable)'
                : 'Green = positive price movement + above-median volume'}
          </div>
        </div>
        {showSectorTab && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {tabBtn('flat', 'Flat')}
            {tabBtn('sector', 'By sector')}
          </div>
        )}
      </div>
      <div style={{
        filter: masked && viewMode === 'flat' ? 'blur(6px)' : undefined,
        userSelect: masked && viewMode === 'flat' ? 'none' : undefined,
      }}>
        {sectorViewActive ? (
          sectorDataReady ? (
            <SectorCoinGrid
              sectors={sectorBreakdown!.sectors}
              isMobile={isMobile}
              masked={masked}
              interactive={enableCoinActions}
              onCoinClick={onCoinClick}
            />
          ) : (
            <div style={{ padding: '28px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              Loading sector breakdown…
            </div>
          )
        ) : (
          <CoinGrid
            coins={participation.coins}
            interactive={enableCoinActions}
            onCoinClick={onCoinClick}
          />
        )}
      </div>
      {showStandaloneFutures && futuresSector && (
        <div style={{
          marginTop: 20,
          filter: masked ? 'blur(6px)' : undefined,
          userSelect: masked ? 'none' : undefined,
        }}>
          <SectorCoinGrid
            sectors={[futuresSector]}
            isMobile={isMobile}
            masked={masked}
            interactive={enableCoinActions}
            onCoinClick={onCoinClick}
          />
        </div>
      )}
    </div>
  );
};

// ── feature highlights ──────────────────────────────────────────────────────

const FeatureCards: React.FC<{ cfg: any }> = ({ cfg }) => {
  const features = [
    {
      show: cfg.showSignalCardOracle !== false,
      icon: cfg.signalCardOracleIcon || '🔮',
      title: cfg.signalCardOracleTitle || 'Oracle Rankings',
      desc: cfg.signalCardOracleDesc || 'AI-scored coin rankings updated in real-time, giving you the highest-conviction LONG and SHORT candidates.',
    },
    {
      show: cfg.showSignalCardBtcRegime !== false,
      icon: cfg.signalCardBtcRegimeIcon || '📊',
      title: cfg.signalCardBtcRegimeTitle || 'BTC Regime Detection',
      desc: cfg.signalCardBtcRegimeDesc || 'Understand whether the macro crypto market is bullish, bearish, or ranging before placing any trade.',
    },
    {
      show: cfg.showSignalCardMicroReversal !== false,
      icon: cfg.signalCardMicroReversalIcon || '⚡',
      title: cfg.signalCardMicroReversalTitle || 'Micro-Reversal Filter',
      desc: cfg.signalCardMicroReversalDesc || 'Blocks the chop window when BTC fake-drops −0.10% to −0.50% from a recent 5m high — the only losing BTC regime we filter.',
    },
    {
      show: cfg.showSignalCardParticipation !== false,
      icon: cfg.signalCardParticipationIcon || '🔥',
      title: cfg.signalCardParticipationTitle || 'Participation Filter',
      desc: cfg.signalCardParticipationDesc || 'Know which coins are actually moving with volume behind them — not just random price noise.',
    },
    {
      show: cfg.showSignalCardRisk !== false,
      icon: cfg.signalCardRiskIcon || '🛡️',
      title: cfg.signalCardRiskTitle || 'Risk-Aware Signals',
      desc: cfg.signalCardRiskDesc || 'RSI, EMA, funding rate and momentum all combined so you never trade against the current.',
    },
  ].filter(f => f.show);

  if (features.length === 0) return null;

  return (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
    {features.map(f => (
      <div key={f.title} style={{
        background: '#fff', borderRadius: 16, padding: '20px 18px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9',
      }}>
        <div style={{ fontSize: 28, marginBottom: 10 }}>{f.icon}</div>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 6 }}>{f.title}</div>
        <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>{f.desc}</div>
      </div>
    ))}
  </div>
  );
};

// ── proof section helpers ───────────────────────────────────────────────────

function safeParseRows<T>(json: string | undefined | null, fallback: T[]): T[] {
  if (!json) return fallback;
  try { return JSON.parse(json) as T[]; } catch { return fallback; }
}

interface ProofRegimeRow { date: string; regime: string; outcome: string; }
interface ProofParticipationRow { label: string; result: string; }
interface ProofSignalRow { coin: string; bias: string; window: string; outcome: string; }

const TH: React.CSSProperties = {
  padding: '8px 12px', background: '#f8fafc', fontWeight: 700,
  fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5,
  borderBottom: '1px solid #e2e8f0', textAlign: 'left',
};
const TD: React.CSSProperties = {
  padding: '10px 12px', fontSize: 13, color: '#1e293b',
  borderBottom: '1px solid #f1f5f9',
};

interface ProofRegimeSectionProps { cfg: any; isMobile: boolean; redactSensitive?: boolean }
const ProofRegimeSection: React.FC<ProofRegimeSectionProps> = ({ cfg, isMobile, redactSensitive }) => {
  const rows = redactSensitive
    ? ([
        { date: '████-██-██', regime: '██████', outcome: '████████████' },
        { date: '████-██-██', regime: '██████', outcome: '████████████' },
        { date: '████-██-██', regime: '██████', outcome: '████████████' },
        { date: '████-██-██', regime: '██████', outcome: '████████████' },
      ] as ProofRegimeRow[])
    : safeParseRows<ProofRegimeRow>(cfg.proofRegimeRows, [
        { date: 'Day 1', regime: 'Ranging',  outcome: 'Chop (no trend) ✅' },
        { date: 'Day 2', regime: 'Trending', outcome: 'Strong move ✅' },
      ]);
  const tableWrap = redactSensitive ? { ...REDACTED_SIGNAL_BLUR, marginTop: 14 } : { marginTop: 14 };
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 16 : 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: isMobile ? 20 : 24 }}>📊</span>
        <div>
          <div style={{ fontWeight: 800, fontSize: isMobile ? 15 : 17, color: '#1e293b' }}>
            {cfg.proofRegimeTitle || 'Market Condition Accuracy'}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {cfg.proofRegimeSubtitle || 'Track when our system calls Trending vs Ranging and what actually happens next'}
          </div>
        </div>
        {cfg.proofRegimeAccuracy && (
          <span style={{ marginLeft: 'auto', background: '#eff6ff', color: '#1e40af', fontWeight: 800, fontSize: isMobile ? 18 : 22, padding: '6px 16px', borderRadius: 12 }}>
            {redactSensitive ? '██%' : cfg.proofRegimeAccuracy} accuracy
          </span>
        )}
      </div>
      {rows.length > 0 && (
        <div style={{ overflowX: 'auto', ...tableWrap }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <thead>
              <tr>
                <th style={TH}>Date</th>
                <th style={TH}>Regime Called</th>
                <th style={TH}>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={TD}>{r.date}</td>
                  <td style={TD}>
                    <span style={{
                      background: redactSensitive ? '#f1f5f9' : (r.regime?.toLowerCase().includes('trend') ? '#d1fae5' : '#fef3c7'),
                      color:      redactSensitive ? '#94a3b8' : (r.regime?.toLowerCase().includes('trend') ? '#065f46' : '#78350f'),
                      fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 20,
                    }}>{r.regime}</span>
                  </td>
                  <td style={TD}>{r.outcome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

interface ProofParticipationSectionProps { cfg: any; isMobile: boolean; redactSensitive?: boolean }
const ProofParticipationSection: React.FC<ProofParticipationSectionProps> = ({ cfg, isMobile, redactSensitive }) => {
  const rows = redactSensitive
    ? ([
        { label: '████████████', result: '████' },
        { label: '████████████', result: '████' },
      ] as ProofParticipationRow[])
    : safeParseRows<ProofParticipationRow>(cfg.proofParticipationRows, [
        { label: 'HOT coins avg move',  result: '+12%' },
        { label: 'COOL coins avg move', result: '+2%'  },
      ]);
  const statsBlur = redactSensitive ? REDACTED_SIGNAL_BLUR : {};
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 16 : 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: isMobile ? 20 : 24 }}>🔥</span>
        <div>
          <div style={{ fontWeight: 800, fontSize: isMobile ? 15 : 17, color: '#1e293b' }}>
            {cfg.proofParticipationTitle || 'Participation Filter Effectiveness'}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {cfg.proofParticipationSubtitle || 'HOT coins vs COOL coins — where real momentum lives'}
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(2, minmax(0, 200px))', gap: 12, marginTop: 14, marginBottom: rows.length > 0 ? 14 : 0, ...statsBlur }}>
        {cfg.proofHotAvg && (
          <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 12, padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>HOT Coins</div>
            <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: '#15803d' }}>{redactSensitive ? '██%' : cfg.proofHotAvg}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>avg move</div>
          </div>
        )}
        {cfg.proofCoolAvg && (
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>COOL Coins</div>
            <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: '#475569' }}>{redactSensitive ? '██%' : cfg.proofCoolAvg}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>avg move</div>
          </div>
        )}
      </div>
      {rows.length > 0 && (
        <div style={{ overflowX: 'auto', ...(redactSensitive ? REDACTED_SIGNAL_BLUR : {}) }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <thead>
              <tr>
                <th style={TH}>Filter Category</th>
                <th style={TH}>Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={TD}>{r.label}</td>
                  <td style={{ ...TD, fontWeight: 700, color: redactSensitive ? '#94a3b8' : (r.result?.startsWith('+') ? '#15803d' : '#1e293b') }}>{r.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

interface ProofSignalsSectionProps { cfg: any; isMobile: boolean; redactSensitive?: boolean }
const ProofSignalsSection: React.FC<ProofSignalsSectionProps> = ({ cfg, isMobile, redactSensitive }) => {
  const rows = redactSensitive
    ? ([
        { coin: '████', bias: '██████', window: '████', outcome: '████████' },
        { coin: '████', bias: '██████', window: '████', outcome: '████████' },
        { coin: '████', bias: '██████', window: '████', outcome: '████████' },
      ] as ProofSignalRow[])
    : safeParseRows<ProofSignalRow>(cfg.proofSignalRows, []);
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 16 : 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: isMobile ? 20 : 24 }}>🎯</span>
        <div>
          <div style={{ fontWeight: 800, fontSize: isMobile ? 15 : 17, color: '#1e293b' }}>
            {cfg.proofSignalsTitle || 'Signal Quality'}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {cfg.proofSignalsSubtitle || 'Bullish/Bearish bias tracked over a fixed window — win rate, avg move and max drawdown'}
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 180px))', gap: 12, marginTop: 14, marginBottom: rows.length > 0 ? 14 : 0, ...(redactSensitive ? REDACTED_SIGNAL_BLUR : {}) }}>
        {cfg.proofSignalWinRate && (
          <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 12, padding: '14px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Win Rate</div>
            <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: '#1e40af' }}>{redactSensitive ? '██%' : cfg.proofSignalWinRate}</div>
          </div>
        )}
        {cfg.proofSignalAvgMove && (
          <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 12, padding: '14px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Avg Move</div>
            <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: '#15803d' }}>{redactSensitive ? '██%' : cfg.proofSignalAvgMove}</div>
          </div>
        )}
        {cfg.proofSignalMaxDD && (
          <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 12, padding: '14px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Max Drawdown</div>
            <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: '#b91c1c' }}>{redactSensitive ? '██%' : cfg.proofSignalMaxDD}</div>
          </div>
        )}
      </div>
      {rows.length > 0 && (
        <div style={{ overflowX: 'auto', ...(redactSensitive ? REDACTED_SIGNAL_BLUR : {}) }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <thead>
              <tr>
                <th style={TH}>Coin</th>
                <th style={TH}>Bias</th>
                <th style={TH}>Window</th>
                <th style={TH}>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ ...TD, fontWeight: 700 }}>{r.coin}</td>
                  <td style={TD}>
                    <span style={{
                      background: redactSensitive ? '#f1f5f9' : (r.bias?.toLowerCase().includes('bull') ? '#d1fae5' : r.bias?.toLowerCase().includes('bear') ? '#fee2e2' : '#f1f5f9'),
                      color:      redactSensitive ? '#94a3b8' : (r.bias?.toLowerCase().includes('bull') ? '#065f46' : r.bias?.toLowerCase().includes('bear') ? '#7f1d1d' : '#475569'),
                      fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 20,
                    }}>{r.bias}</span>
                  </td>
                  <td style={TD}>{r.window}</td>
                  <td style={TD}>{r.outcome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

type WinningTradesColumnKey =
  | 'pair'
  | 'side'
  | 'closed'
  | 'entryExit'
  | 'strategy'
  | 'reason'
  | 'dailyPnl'
  | 'dailyPnlPerStrategy'
  | 'tradePnl';

const WINNING_TRADES_COLUMN_HELP: Record<
  WinningTradesColumnKey,
  { label: string; description: string }
> = {
  pair: {
    label: 'Pair',
    description: 'The symbol that was traded (e.g. PEPE, DOGS).',
  },
  side: {
    label: 'Side',
    description: 'Position direction when the trade closed. Often shown as CLOSE for a closed leg.',
  },
  closed: {
    label: 'Closed',
    description: 'Date and time the position was closed. Click the column header to sort oldest or newest first.',
  },
  entryExit: {
    label: 'Entry → Exit',
    description: 'Open price and close price for this position (USDT terms).',
  },
  strategy: {
    label: 'Strategy',
    description: 'Name of the active bot/strategy that ran this trade (e.g. Oracle).',
  },
  reason: {
    label: 'Reason',
    description: 'Why the bot closed the trade — for example TAKE_PROFIT, TRAILING_STOP, or reconciliation from exchange history.',
  },
  dailyPnl: {
    label: 'Daily PNL (all bots)',
    description:
      'Net realized profit or loss for the entire UTC calendar day across all active-bot closes in our calculation pool — not just this row. The same value appears on every row that closed on that UTC date (any strategy). It can be negative even when Trade PNL on this row is positive, because other trades the same day are included.',
  },
  dailyPnlPerStrategy: {
    label: 'Daily PNL (strategy)',
    description:
      'Net realized profit or loss for the UTC close date for this row\'s strategy only (all closes for that bot in our pool that day — not just this row). The same value appears on every row with the same strategy that closed on that UTC date. Can differ from Daily PNL (all bots) on the same date.',
  },
  tradePnl: {
    label: 'Trade PNL',
    description: 'Profit or loss for this single closed trade only. Green = win, red = loss.',
  },
};

interface WinningTradesHelpModalProps {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  redactSensitive?: boolean;
  showStrategyName?: boolean;
  showDailyPnl?: boolean;
  showDailyPnlPerStrategy?: boolean;
  includeLosingTrades?: boolean;
  displayLimit?: number;
}

const WinningTradesHelpModal: React.FC<WinningTradesHelpModalProps> = ({
  open,
  onClose,
  isMobile,
  redactSensitive,
  showStrategyName,
  showDailyPnl,
  showDailyPnlPerStrategy,
  includeLosingTrades,
  displayLimit = 0,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const limitLabel =
    displayLimit === 2
      ? 'the 2 most recent qualifying trades'
      : displayLimit === 5
        ? 'the 5 most recent qualifying trades'
        : 'up to 20 recent qualifying trades';

  const visibleColumnKeys: WinningTradesColumnKey[] = [
    'pair',
    'side',
    'closed',
    'entryExit',
    ...(showStrategyName ? (['strategy'] as const) : []),
    'reason',
    ...(showDailyPnl ? (['dailyPnl'] as const) : []),
    ...(showDailyPnlPerStrategy ? (['dailyPnlPerStrategy'] as const) : []),
    'tradePnl',
  ];

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? 16 : 24,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="winning-trades-help-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 520,
          maxHeight: 'min(88vh, 640px)',
          overflow: 'auto',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
          padding: isMobile ? 20 : 28,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <h3
            id="winning-trades-help-title"
            style={{ margin: 0, fontSize: isMobile ? 17 : 19, fontWeight: 800, color: '#1e293b', lineHeight: 1.3 }}
          >
            Understanding Winning Trades
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none',
              background: '#f1f5f9',
              color: '#475569',
              borderRadius: 8,
              width: 32,
              height: 32,
              fontSize: 18,
              lineHeight: 1,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {redactSensitive ? (
          <p style={{ margin: 0, fontSize: 14, color: '#64748b', lineHeight: 1.65 }}>
            Trade details are hidden on this public view (placeholder data only). Sign in or contact us to see live
            closed trades and full analytics.
          </p>
        ) : (
          <>
            <p style={{ margin: '0 0 14px', fontSize: 14, color: '#475569', lineHeight: 1.65 }}>
              These rows are <strong>real closed positions</strong> from <strong>active strategy bots</strong>, using the
              same sources as bot analytics. The table shows {limitLabel}
              {includeLosingTrades ? ', including losses when enabled.' : ' (profitable closes only by default).'}
            </p>

            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
              Only columns visible in your table are described below. Column visibility is configured by the site
              administrator.
            </p>

            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Columns in this table
            </div>
            <ul style={{ margin: '0 0 18px', paddingLeft: 18, listStyle: 'disc' }}>
              {visibleColumnKeys.map((key) => {
                const col = WINNING_TRADES_COLUMN_HELP[key];
                return (
                  <li key={key} style={{ marginBottom: 10, fontSize: 13, color: '#334155', lineHeight: 1.55 }}>
                    <strong style={{ color: '#1e293b' }}>{col.label}</strong>
                    {' — '}
                    {col.description}
                  </li>
                );
              })}
            </ul>

            {(showDailyPnl || showDailyPnlPerStrategy) && (
              <div
                style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 12,
                  padding: '12px 14px',
                  marginBottom: 16,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e40af', marginBottom: 6 }}>
                  Trade PNL vs Daily PNL columns
                </div>
                <p style={{ margin: '0 0 8px', fontSize: 13, color: '#334155', lineHeight: 1.55 }}>
                  <strong>Trade PNL</strong> = one position.
                  {showDailyPnl && (
                    <>
                      {' '}
                      <strong>Daily PNL (all bots)</strong> = whole UTC day across all active bots in the calculation pool.
                    </>
                  )}
                  {showDailyPnlPerStrategy && (
                    <>
                      {' '}
                      <strong>Daily PNL (strategy)</strong> = whole UTC day for <strong>this row&apos;s strategy only</strong>.
                    </>
                  )}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: '#475569', lineHeight: 1.55 }}>
                  Daily totals include closes that day even if they are not listed in this table. Two small wins can still
                  show a <strong>negative</strong> daily figure if other closes that day (same scope: all bots or this
                  strategy) lost more overall.
                  {!includeLosingTrades && showDailyPnl && (
                    <>
                      {' '}
                      With losing trades hidden from the table, <strong>Daily PNL (all bots)</strong> may still reflect
                      those losses when data comes from the database snapshot.
                    </>
                  )}
                </p>
              </div>
            )}

            <p style={{ margin: 0, fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
              Figures are realized PNL at close. Not financial advice. Refresh cadence follows the section subtitle
              above the table.
            </p>
          </>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 20,
            width: '100%',
            padding: '10px 16px',
            border: 'none',
            borderRadius: 999,
            background: '#086afb',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
};

interface ProofWinningTradesSectionProps {
  cfg: any;
  isMobile: boolean;
  redactSensitive?: boolean;
  trades: WinningTradeRow[];
  currentPage: number;
  onPageChange: (next: number) => void;
  showStrategyName?: boolean;
  showDailyPnl?: boolean;
  showDailyPnlPerStrategy?: boolean;
  includeLosingTrades?: boolean;
  displayLimit?: number;
}
const ProofWinningTradesSection: React.FC<ProofWinningTradesSectionProps> = ({
  cfg,
  isMobile,
  redactSensitive,
  trades,
  currentPage,
  onPageChange,
  showStrategyName,
  showDailyPnl,
  showDailyPnlPerStrategy,
  includeLosingTrades,
  displayLimit,
}) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const [closedSort, setClosedSort] = useState<'desc' | 'asc'>('desc');
  const sortedRows = useMemo(() => {
    const copy = [...trades.slice(0, 20)];
    copy.sort((a, b) => {
      const ta = a.closedAt ? new Date(a.closedAt).getTime() : 0;
      const tb = b.closedAt ? new Date(b.closedAt).getTime() : 0;
      return closedSort === 'asc' ? ta - tb : tb - ta;
    });
    return copy;
  }, [trades, closedSort]);
  const allRows = sortedRows;
  const perPage = 5;
  const totalPages = Math.max(1, Math.ceil(allRows.length / perPage));
  const page = Math.min(Math.max(1, currentPage), totalPages);
  const start = (page - 1) * perPage;
  const rows = allRows.slice(start, start + perPage);

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 16 : 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24 }}>
      <WinningTradesHelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        isMobile={isMobile}
        redactSensitive={redactSensitive}
        showStrategyName={showStrategyName}
        showDailyPnl={showDailyPnl}
        showDailyPnlPerStrategy={showDailyPnlPerStrategy}
        includeLosingTrades={includeLosingTrades}
        displayLimit={displayLimit}
      />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: isMobile ? 20 : 24 }}>🏆</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: isMobile ? 15 : 17, color: '#1e293b' }}>
              {cfg.proofWinningTradesTitle || 'Winning Trades'}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {cfg.proofWinningTradesSubtitle || 'Most recent profitable closes from active strategy bots (same live exchange data as bot analytics)'}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          style={{
            border: '1px solid #bfdbfe',
            background: '#eff6ff',
            color: '#2563eb',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            padding: '8px 14px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          ⓘ How to read this table
        </button>
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: '#94a3b8', padding: '10px 0 4px' }}>
          No winning trades available yet.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', ...(redactSensitive ? REDACTED_SIGNAL_BLUR : {}) }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <thead>
              <tr>
                <th style={TH}>Pair</th>
                <th style={TH}>Side</th>
                <th style={TH}>
                  <button
                    onClick={() => setClosedSort((s) => (s === 'desc' ? 'asc' : 'desc'))}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      margin: 0,
                      color: '#64748b',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                    }}
                    title={`Sort by closed date (${closedSort === 'desc' ? 'Newest first' : 'Oldest first'})`}
                  >
                    Closed {closedSort === 'desc' ? '↓' : '↑'}
                  </button>
                </th>
                <th style={TH}>Entry → Exit</th>
                {showStrategyName && <th style={TH}>Strategy</th>}
                <th style={TH}>Reason</th>
                {showDailyPnl && <th style={{ ...TH, textAlign: 'right' }}>Daily PNL (all)</th>}
                {showDailyPnlPerStrategy && (
                  <th style={{ ...TH, textAlign: 'right' }}>Daily PNL (strategy)</th>
                )}
                <th style={{ ...TH, textAlign: 'right' }}>Trade PNL</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.symbol}-${start + i}`} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ ...TD, fontWeight: 700 }}>{r.symbol}</td>
                  <td style={TD}>
                    <span style={{
                      background: r.side === 'LONG' ? '#d1fae5' : r.side === 'SHORT' ? '#fee2e2' : '#f1f5f9',
                      color: r.side === 'LONG' ? '#065f46' : r.side === 'SHORT' ? '#7f1d1d' : '#475569',
                      fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 20,
                    }}>{redactSensitive ? '████' : r.side}</span>
                  </td>
                  <td style={TD}>{redactSensitive ? '████ ██:██' : fmtDateTime(r.closedAt)}</td>
                  <td style={TD}>
                    {redactSensitive ? '█.██████ → █.██████' : `$${r.entryPrice.toFixed(6)} → $${r.exitPrice.toFixed(6)}`}
                  </td>
                  {showStrategyName && (
                    <td style={TD}>{redactSensitive ? '████ ████' : (r.strategyName || 'Strategy')}</td>
                  )}
                  <td style={TD}>{redactSensitive ? '████████' : (r.exitReason || 'CLOSE')}</td>
                  {showDailyPnl && (
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: pnlColor(r.dailyPnl ?? 0) }}>
                      {fmtPnl(r.dailyPnl ?? 0, redactSensitive)}
                    </td>
                  )}
                  {showDailyPnlPerStrategy && (
                    <td
                      style={{
                        ...TD,
                        textAlign: 'right',
                        fontWeight: 700,
                        color: pnlColor(r.dailyPnlPerStrategy ?? 0),
                      }}
                    >
                      {fmtPnl(r.dailyPnlPerStrategy ?? 0, redactSensitive)}
                    </td>
                  )}
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 800, color: pnlColor(r.pnl) }}>
                    {fmtPnl(r.pnl, redactSensitive)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            Showing {start + 1}-{Math.min(start + perPage, allRows.length)} of {allRows.length}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#334155', borderRadius: 8, fontSize: 12, fontWeight: 700, padding: '6px 10px', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
            >
              Prev
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#334155', borderRadius: 8, fontSize: 12, fontWeight: 700, padding: '6px 10px', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── main page ──────────────────────────────────────────────────────────────

const MarketLanding: React.FC<{ isAdminMarketSignalsEmbed?: boolean }> = ({
  isAdminMarketSignalsEmbed = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const profile = useAppSelector((s) => s.profile.profile);
  const adminUser = useAppSelector((s) => s.auth.adminUser);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const authBootstrapping = useAppSelector((s) => s.auth.isLoading);

  /** Logo + MARKET SIGNALS + Login/Register — only on standalone landing (`/`) and review (`/market` guest or admin-only). Hidden in banking shell (retail `/market` in-app) and admin dashboard embed. */
  const showLandingNavBar = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith('/admin')) return false;
    if (p === '/market' && isAuthenticated && !adminUser) return false;
    return true;
  }, [location.pathname, isAuthenticated, adminUser]);

  /** About section only makes sense on the real landing page — not when the page is
   *  embedded inside the banking app shell (retail /market) or the admin dashboard. */
  const isLandingContext = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith('/admin')) return false;
    if (p === '/market' && isAuthenticated && !adminUser) return false;
    return true;
  }, [location.pathname, isAuthenticated, adminUser]);
  const permissionWatchKey = `${((profile as any)?.permissions || []).join(',')}|${(adminUser?.permissions || []).join(',')}|${adminUser?.role || ''}|${adminUser?.username || ''}`;

  // /market is always the admin preview URL — never redirect even if unpublished
  // / is the public-facing route — only show when confirmed published
  const isPreview = location.pathname === '/market';
  const isPreviewRef = useRef(isPreview);

  useEffect(() => {
    isPreviewRef.current = isPreview;
  }, [isPreview]);

  const [snapshot, setSnapshot] = useState<MarketSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [winningTradesPage, setWinningTradesPage] = useState(1);
  const [selectedCoin, setSelectedCoin] = useState<CoinActivity | null>(null);
  const [coinDrawerOpen, setCoinDrawerOpen] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const marketDataRequestIdRef = useRef(0);
  const redirectedRef = useRef(false);
  const isAdminEmbedRef = useRef(isAdminMarketSignalsEmbed);
  const authBootstrappedRef = useRef(false);

  useEffect(() => {
    isAdminEmbedRef.current = isAdminMarketSignalsEmbed;
  }, [isAdminMarketSignalsEmbed]);

  const doRedirect = useCallback(() => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    navigate('/login', { replace: true });
  }, [navigate]);

  const loadMarketData = useCallback(async () => {
    const requestId = ++marketDataRequestIdRef.current;
    try {
      const mode = resolvePrivilegedMarketFetch();
      const data =
        mode === 'admin'
          ? await publicMarketService.getAdminMarketLandingState()
          : mode === 'retail'
            ? await publicMarketService.getRetailLandingState()
            : await publicMarketService.getFullSnapshot();
      if (requestId !== marketDataRequestIdRef.current) {
        return;
      }
      setSnapshot((prev) => mergeLandingMarketSnapshot(prev, data));
      setLastRefresh(new Date());
      setError(null);
    } catch {
      if (requestId !== marketDataRequestIdRef.current) {
        return;
      }
      if (!isAdminEmbedRef.current) {
        setError('Signal data temporarily unavailable');
      }
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const mode = resolvePrivilegedMarketFetch();
      if (mode) {
        try {
          const data =
            mode === 'admin'
              ? await publicMarketService.getAdminMarketLandingState()
              : await publicMarketService.getRetailLandingState();
          if (!isPreviewRef.current && !data.published) {
            doRedirect();
            return;
          }
          setSnapshot(data as any);
          setLoading(false);
          setError(null);
          // Also kick off loadMarketData so the 45-second polling loop keeps
          // winning trades current and responds to maskWinningTrades toggle changes.
          loadMarketData();
          return;
        } catch {
          if (isAdminEmbedRef.current || mode === 'admin') {
            setError('Signal data temporarily unavailable');
            setLoading(false);
            return;
          }
          /* fall through to public config for unprivileged landing */
        }
      }

      const cfg = await publicMarketService.getLandingConfig();
      if (!isPreviewRef.current && !cfg.published) {
        doRedirect();
        return;
      }
      setSnapshot(cfg as any);
      setLoading(false);
      // Always attempt to load market data so that Winning Trades can be
      // fetched independently of the global maskAllSections gate.
      // The backend will only include winningTrades in the response when
      // maskWinningTrades is false, so nothing leaks even when maskAllSections is on.
      loadMarketData();
    } catch {
      if (isAdminEmbedRef.current) {
        setLoading(false);
        setError('Signal data temporarily unavailable');
        return;
      }
      // P3: API unreachable — render with safe defaults instead of /login redirect loop.
      setSnapshot(FALLBACK_LANDING_CONFIG as MarketSnapshot);
      setLoading(false);
      setError(
        'Unable to reach api.zogzila.com — showing default content. Live signals are temporarily unavailable.'
      );
    }
  }, [doRedirect, loadMarketData]);

  useEffect(() => {
    if (authBootstrapping && !authBootstrappedRef.current) {
      return;
    }
    authBootstrappedRef.current = true;
    load();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(loadMarketData, 45_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [load, loadMarketData, permissionWatchKey]);

  useEffect(() => {
    setWinningTradesPage(1);
  }, [(snapshot as any)?.winningTrades, (snapshot as any)?.showProofWinningTrades]);

  // Coin drawer handlers — must run before any early return (rules-of-hooks).
  const signalViewerForActions = (snapshot as any)?.signalViewer === true;
  const participationMaskedForActions =
    (snapshot as any)?.maskParticipation === true && !snapshot?.participation;

  const enableCoinActions =
    signalViewerForActions
    && isAuthenticated
    && !participationMaskedForActions
    && (
      (location.pathname === '/market' && !adminUser)
      || isAdminMarketSignalsEmbed
    );

  const canOpenPositions = useMemo(() => {
    if (!enableCoinActions) return false;
    if (isAdminMarketSignalsEmbed || adminUser) {
      return adminCanOpenMarketSignalPositions(adminUser ?? undefined);
    }
    const perms: string[] = (profile as any)?.permissions ?? [];
    return perms.includes('VIEW_RETAIL_TRADING_BOTS');
  }, [enableCoinActions, isAdminMarketSignalsEmbed, adminUser, profile]);

  const handleCoinClick = useCallback((coin: CoinActivity) => {
    if (!enableCoinActions) return;
    setSelectedCoin(coin);
    setCoinDrawerOpen(true);
  }, [enableCoinActions]);

  const handleCloseCoinDrawer = useCallback(() => {
    setCoinDrawerOpen(false);
  }, []);

  const handleViewStrategyFromDrawer = useCallback((strategyId: number, symbol: string) => {
    setCoinDrawerOpen(false);
    if (isAdminMarketSignalsEmbed || adminUser) {
      navigate(`/admin/dashboard?tab=tradingBots&strategyId=${strategyId}&symbol=${encodeURIComponent(symbol)}`);
    } else {
      navigate(`/trading-bots?strategyId=${strategyId}&symbol=${encodeURIComponent(symbol)}`);
    }
  }, [navigate, isAdminMarketSignalsEmbed, adminUser]);

  // Full-page loader for every route (including /market preview): avoids hero/nav default "template" copy
  // while snapshot is still null. Covers refetches after load() sets loading true again.
  if (authBootstrapping || loading) {
    return (
      <div
        style={{
          minHeight: isAdminMarketSignalsEmbed ? 220 : '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8fafc',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <svg style={{ width: 36, height: 36, animation: 'spin 1s linear infinite' }} viewBox="0 0 24 24" fill="none">
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          <circle cx="12" cy="12" r="10" stroke="#e2e8f0" strokeWidth="4" />
          <path d="M4 12a8 8 0 018-8" stroke="#3b82f6" strokeWidth="4" strokeLinecap="round" />
        </svg>
      </div>
    );
  }

  const cfg = {
    maskAllSections:    (snapshot as any)?.maskAllSections    ?? false,
    showPublicRefreshBanner: (snapshot as any)?.showPublicRefreshBanner ?? true,
    heroTitle:          (snapshot as any)?.heroTitle          || 'Know When the Market is Alive',
    heroSubtitle:       (snapshot as any)?.heroSubtitle       || 'Real-time Oracle rankings. BTC regime. Meme participation. Trade smarter on any platform.',
    heroCtaPrimary:     (snapshot as any)?.heroCtaPrimary     || 'Open Trading Account',
    heroCtaSecondary:   (snapshot as any)?.heroCtaSecondary   || 'Sign In',
    heroCtaPrimaryUrl:  (snapshot as any)?.heroCtaPrimaryUrl  || '/register',
    heroCtaSecondaryUrl:(snapshot as any)?.heroCtaSecondaryUrl|| '/login',
    showCtaPrimary:     (snapshot as any)?.showCtaPrimary  ?? true,
    showCtaSecondary:   (snapshot as any)?.showCtaSecondary ?? true,
    navBrandText:       (snapshot as any)?.navBrandText       || 'Zogzila Client',
    navTagline:         (snapshot as any)?.navTagline         || 'MARKET SIGNALS',
    navBtn1Label:       (snapshot as any)?.navBtn1Label       || 'Login',
    navBtn1Url:         (snapshot as any)?.navBtn1Url         || '/login',
    navBtn1Visible:     (snapshot as any)?.navBtn1Visible  ?? true,
    navBtn2Label:       (snapshot as any)?.navBtn2Label       || 'Register',
    navBtn2Url:         (snapshot as any)?.navBtn2Url         || '/register/personal',
    navBtn2Visible:     (snapshot as any)?.navBtn2Visible  ?? true,
    navBtn3Label:       (snapshot as any)?.footerLink4Label   || 'Business Register',
    navBtn3Url:         (snapshot as any)?.footerLink4Url     || '/register/business',
    navBtn3Visible:     (snapshot as any)?.footerLink4Visible ?? true,
    showOracleFeed:     (snapshot as any)?.showOracleFeed  ?? true,
    showBtcFilter:      (snapshot as any)?.showBtcFilter   ?? true,
    showMicroReversalFilter: (snapshot as any)?.showMicroReversalFilter ?? false,
    showParticipation:  (snapshot as any)?.showParticipation ?? true,
    showAiRegime:       (snapshot as any)?.showAiRegime      ?? true,
    maskAiRegime:       (snapshot as any)?.maskAiRegime      ?? false,
    showMajorCoinsMarket: (snapshot as any)?.showMajorCoinsMarket ?? true,
    maskMajorCoinsMarket: (snapshot as any)?.maskMajorCoinsMarket ?? false,
    showMajorCoinsChartStyleOptions: (snapshot as any)?.showMajorCoinsChartStyleOptions ?? true,
    majorCoinsDefaultChart: (snapshot as any)?.majorCoinsDefaultChart ?? 'donut',
    showSectorBreakdown: (snapshot as any)?.showSectorBreakdown ?? false,
    footerCopyright:    (snapshot as any)?.footerCopyright    || `© ${new Date().getFullYear()} ${branding.sidebar.logoText} · Signal data via KuCoin public API`,
    footerLink1Label:   (snapshot as any)?.footerLink1Label   || 'Login',
    footerLink1Url:     (snapshot as any)?.footerLink1Url     || '/login',
    footerLink1Visible: (snapshot as any)?.footerLink1Visible ?? true,
    footerLink2Label:   (snapshot as any)?.footerLink2Label   || 'Register',
    footerLink2Url:     (snapshot as any)?.footerLink2Url     || '/register',
    footerLink2Visible: (snapshot as any)?.footerLink2Visible ?? true,
    footerLink3Label:   (snapshot as any)?.footerLink3Label   || 'Personal',
    footerLink3Url:     (snapshot as any)?.footerLink3Url     || '/register/personal',
    footerLink3Visible: (snapshot as any)?.footerLink3Visible ?? true,
    footerLink4Label:   (snapshot as any)?.footerLink4Label   || 'Business',
    footerLink4Url:     (snapshot as any)?.footerLink4Url     || '/business/register',
    footerLink4Visible: (snapshot as any)?.footerLink4Visible ?? true,
    // Bottom CTA banner — independent from hero
    ctaBannerTitle:    (snapshot as any)?.ctaBannerTitle    || 'Ready to Trade with an Edge?',
    ctaBannerSubtitle: (snapshot as any)?.ctaBannerSubtitle || 'Join our trading platform to access automated strategies, personalised signal feeds, and real-time portfolio management.',
    // Proof sections
    showProofRegime:          (snapshot as any)?.showProofRegime          ?? false,
    proofRegimeTitle:         (snapshot as any)?.proofRegimeTitle         || 'Market Condition Accuracy',
    proofRegimeSubtitle:      (snapshot as any)?.proofRegimeSubtitle      || 'Track when our system calls Trending vs Ranging and what actually happens next',
    proofRegimeAccuracy:      (snapshot as any)?.proofRegimeAccuracy      || '',
    proofRegimeRows:          (snapshot as any)?.proofRegimeRows          || '',
    showProofParticipation:   (snapshot as any)?.showProofParticipation   ?? false,
    proofParticipationTitle:  (snapshot as any)?.proofParticipationTitle  || 'Participation Filter Effectiveness',
    proofParticipationSubtitle:(snapshot as any)?.proofParticipationSubtitle || 'HOT coins vs COOL coins — where real momentum lives',
    proofHotAvg:              (snapshot as any)?.proofHotAvg              || '',
    proofCoolAvg:             (snapshot as any)?.proofCoolAvg             || '',
    proofParticipationRows:   (snapshot as any)?.proofParticipationRows   || '',
    showProofSignals:         (snapshot as any)?.showProofSignals         ?? false,
    proofSignalsTitle:        (snapshot as any)?.proofSignalsTitle        || 'Signal Quality',
    proofSignalsSubtitle:     (snapshot as any)?.proofSignalsSubtitle     || 'Bullish/Bearish bias tracked over a fixed window — win rate, avg move and max drawdown',
    proofSignalWinRate:       (snapshot as any)?.proofSignalWinRate       || '',
    proofSignalAvgMove:       (snapshot as any)?.proofSignalAvgMove       || '',
    proofSignalMaxDD:         (snapshot as any)?.proofSignalMaxDD         || '',
    proofSignalRows:          (snapshot as any)?.proofSignalRows          || '',
    showProofWinningTrades:   (snapshot as any)?.showProofWinningTrades   ?? false,
    proofWinningTradesTitle:  (snapshot as any)?.proofWinningTradesTitle  || 'Winning Trades',
    proofWinningTradesSubtitle:(snapshot as any)?.proofWinningTradesSubtitle || 'Most recent profitable closes from active strategy bots (same live exchange data as bot analytics)',
    showProofWinningTradesStrategyName: (snapshot as any)?.showProofWinningTradesStrategyName ?? true,
    showProofLosingTrades:          (snapshot as any)?.showProofLosingTrades          ?? false,
    showProofWinningTradesDailyPnl: (snapshot as any)?.showProofWinningTradesDailyPnl ?? true,
    showProofWinningTradesDailyPnlPerStrategy:
      (snapshot as any)?.showProofWinningTradesDailyPnlPerStrategy ?? false,
    maskWinningTrades:              (snapshot as any)?.maskWinningTrades              ?? false,
    maskBtcRegime:                  (snapshot as any)?.maskBtcRegime                  ?? false,
    maskMicroReversal:              (snapshot as any)?.maskMicroReversal              ?? false,
    maskParticipation:              (snapshot as any)?.maskParticipation              ?? false,
    // How Our Signals Work cards
    showSignalCardOracle:           (snapshot as any)?.showSignalCardOracle           ?? true,
    signalCardOracleIcon:           (snapshot as any)?.signalCardOracleIcon           || '🔮',
    signalCardOracleTitle:          (snapshot as any)?.signalCardOracleTitle          || 'Oracle Rankings',
    signalCardOracleDesc:           (snapshot as any)?.signalCardOracleDesc           || 'AI-scored coin rankings updated in real-time, giving you the highest-conviction LONG and SHORT candidates.',
    showSignalCardBtcRegime:        (snapshot as any)?.showSignalCardBtcRegime        ?? true,
    signalCardBtcRegimeIcon:        (snapshot as any)?.signalCardBtcRegimeIcon        || '📊',
    signalCardBtcRegimeTitle:       (snapshot as any)?.signalCardBtcRegimeTitle       || 'BTC Regime Detection',
    signalCardBtcRegimeDesc:        (snapshot as any)?.signalCardBtcRegimeDesc        || 'Understand whether the macro crypto market is bullish, bearish, or ranging before placing any trade.',
    showSignalCardMicroReversal:    (snapshot as any)?.showSignalCardMicroReversal    ?? true,
    signalCardMicroReversalIcon:    (snapshot as any)?.signalCardMicroReversalIcon    || '⚡',
    signalCardMicroReversalTitle:   (snapshot as any)?.signalCardMicroReversalTitle   || 'Micro-Reversal Filter',
    signalCardMicroReversalDesc:    (snapshot as any)?.signalCardMicroReversalDesc    || 'Blocks the chop window when BTC fake-drops −0.10% to −0.50% from a recent 5m high — the only losing BTC regime we filter.',
    showSignalCardParticipation:    (snapshot as any)?.showSignalCardParticipation    ?? true,
    signalCardParticipationIcon:    (snapshot as any)?.signalCardParticipationIcon    || '🔥',
    signalCardParticipationTitle:   (snapshot as any)?.signalCardParticipationTitle   || 'Participation Filter',
    signalCardParticipationDesc:    (snapshot as any)?.signalCardParticipationDesc    || 'Know which coins are actually moving with volume behind them — not just random price noise.',
    showSignalCardRisk:             (snapshot as any)?.showSignalCardRisk             ?? true,
    signalCardRiskIcon:             (snapshot as any)?.signalCardRiskIcon             || '🛡️',
    signalCardRiskTitle:            (snapshot as any)?.signalCardRiskTitle            || 'Risk-Aware Signals',
    signalCardRiskDesc:             (snapshot as any)?.signalCardRiskDesc             || 'RSI, EMA, funding rate and momentum all combined so you never trade against the current.',
    winningTradesDisplayLimit:      Number((snapshot as any)?.winningTradesDisplayLimit) || 0,
    // About section
    showAboutSection:        (snapshot as any)?.showAboutSection        ?? false,
    maskAboutSection:        (snapshot as any)?.maskAboutSection        ?? false,
    aboutBadge:              (snapshot as any)?.aboutBadge              || 'About Zogzila',
    aboutTitle:              (snapshot as any)?.aboutTitle              || 'What is Zogzila?',
    aboutBody:               (snapshot as any)?.aboutBody               || '',
    aboutHighlight1Icon:     (snapshot as any)?.aboutHighlight1Icon     || '📡',
    aboutHighlight1Title:    (snapshot as any)?.aboutHighlight1Title    || 'Real-Time Signals',
    aboutHighlight1Text:     (snapshot as any)?.aboutHighlight1Text     || '',
    aboutHighlight2Icon:     (snapshot as any)?.aboutHighlight2Icon     || '🧠',
    aboutHighlight2Title:    (snapshot as any)?.aboutHighlight2Title    || 'AI-Powered Analysis',
    aboutHighlight2Text:     (snapshot as any)?.aboutHighlight2Text     || '',
    aboutHighlight3Icon:     (snapshot as any)?.aboutHighlight3Icon     || '🏆',
    aboutHighlight3Title:    (snapshot as any)?.aboutHighlight3Title    || 'Proven Results',
    aboutHighlight3Text:     (snapshot as any)?.aboutHighlight3Text     || '',
  };

  const signalViewer = (snapshot as any)?.signalViewer === true;
  /** Public page only: show full layout with dummy + blurred signal values (no empty placeholder). */
  const signalDataRedacted = cfg.maskAllSections === true && !signalViewer;
  const oracleLimitCfg = Number((snapshot as any)?.oracleFeedLimit) || 10;
  // BTC Market Regime: show real data if backend sent it; fall back to masked
  // placeholder only when the key is absent (public + individual mask ON).
  const btcRegimeMasked = cfg.maskBtcRegime === true && !snapshot?.btcRegime;
  const btcDisplay = cfg.showBtcFilter
    ? (snapshot?.btcRegime ?? (btcRegimeMasked ? buildMaskedBtcRegime() : undefined))
    : undefined;
  const microReversalMasked = cfg.maskMicroReversal === true && !(snapshot as any)?.microReversal;
  const microReversalDisplay = cfg.showMicroReversalFilter
    ? ((snapshot as any)?.microReversal ?? (microReversalMasked ? buildMaskedMicroReversal() : undefined))
    : undefined;
  const signalsBlockedByMicroReversal =
    (snapshot as any)?.signalsBlockedByMicroReversal === true
    || microReversalDisplay?.blocked === true;
  // Meme Participation: same pattern.
  const participationMasked = cfg.maskParticipation === true && !snapshot?.participation;
  const participationDisplay = cfg.showParticipation
    ? (snapshot?.participation ?? (participationMasked ? buildMaskedParticipation() : undefined))
    : undefined;
  const sectorBreakdownDisplay = cfg.showSectorBreakdown && participationDisplay
    ? ((snapshot as MarketSnapshot)?.sectorBreakdown
        ?? (participationMasked ? buildMaskedSectorBreakdown() : undefined))
    : undefined;

  const futuresActivityDisplay = participationDisplay
    ? ((snapshot as MarketSnapshot)?.futuresActivity
        ?? sectorBreakdownDisplay?.sectors?.find(s => s.id === 'FUTURES')
        ?? (participationMasked ? buildMaskedFuturesActivity() : undefined))
    : undefined;

  // AI Regime: show real data if backend sent it; fall back to masked placeholder when key absent.
  const aiRegimeData = (snapshot as any)?.aiRegime as { regime: string; label: string; updatedAt: number } | undefined;
  const aiRegimeMasked = cfg.maskAiRegime === true && !aiRegimeData;
  const aiRegimeDisplay = cfg.showAiRegime
    ? (aiRegimeData ?? (aiRegimeMasked ? { regime: '██████', label: '▓▓▓▓▓▓▓▓▓▓', updatedAt: Math.floor(Date.now() / 1000) - 120 } : undefined))
    : undefined;
  const majorCoinsMasked = cfg.maskMajorCoinsMarket === true && !snapshot?.majorCoinsMarket;
  const majorCoinsDisplay = cfg.showMajorCoinsMarket
    ? (snapshot?.majorCoinsMarket ?? (majorCoinsMasked ? buildMaskedMajorCoinsMarket() : undefined))
    : undefined;
  const oracleDisplay = cfg.showOracleFeed && !signalsBlockedByMicroReversal
    ? (signalDataRedacted ? buildMaskedOracleFeed(oracleLimitCfg) : snapshot?.oracleFeed)
    : undefined;
  const oracleStockDisplay = cfg.showOracleFeed && !signalsBlockedByMicroReversal
    ? (signalDataRedacted ? buildMaskedOracleFeed(oracleLimitCfg) : snapshot?.oracleFeedStockIndex)
    : undefined;
  // Winning Trades has its own independent mask toggle that works regardless of
  // the global maskAllSections state. signalDataRedacted (global mask) is intentionally
  // NOT ORed here so admins can unmask this section while the global mask is still on.
  const winningTradesMasked = cfg.maskWinningTrades === true;
  const winningTradesDisplayLimit = cfg.winningTradesDisplayLimit; // 0 = all, 2, 5
  const winningTradesDisplay: WinningTradeRow[] = cfg.showProofWinningTrades
    ? winningTradesMasked
      ? buildMaskedWinningTrades(20)
      : (() => {
          const all: WinningTradeRow[] = (snapshot as any)?.winningTrades || [];
          return winningTradesDisplayLimit > 0 ? all.slice(0, winningTradesDisplayLimit) : all;
        })()
    : [];

  const handleCtaClick = (url: string) => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener noreferrer');
    } else {
      navigate(url);
    }
  };

  // ── Paid banners ─────────────────────────────────────────────────────────
  const snap = snapshot as any;
  // Banners: only show on the public landing page (not for authenticated signal viewers)
  const bLeft  = !signalViewer ? readBanners(snap, 'LEFT')  : [];
  const bRight = !signalViewer ? readBanners(snap, 'RIGHT') : [];
  const hasLeftBanners  = !isMobile && bLeft.length  > 0;
  const hasRightBanners = !isMobile && bRight.length > 0;
  const mobileBanners   = isMobile && !signalViewer ? readAllBanners(snap) : [];

  return (
    <div
      style={{
        minHeight: isAdminMarketSignalsEmbed ? 0 : '100vh',
        background: '#f8fafc',
        fontFamily: branding.fontFamily,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >

      {/* ── nav bar (standalone landing / review only) ───────────────────── */}
      {showLandingNavBar && (
        <nav style={{
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(226,232,240,0.8)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          padding: isMobile ? '0 16px' : '0 32px',
          boxShadow: '0 1px 20px rgba(8,106,251,0.06)',
        }}>
          <div style={{
            maxWidth: 1200, margin: '0 auto',
            height: isMobile ? 56 : 68,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>

            {/* ── Left: Logo + wordmark + tagline pill ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, overflow: 'hidden' }}>
              {/* Gradient Z mark */}
              <svg width={isMobile ? 28 : 34} height={isMobile ? 28 : 34} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, filter: 'drop-shadow(0 2px 6px rgba(8,106,251,0.35))' }}>
                <defs>
                  <linearGradient id="zNavGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#086afb" />
                    <stop offset="100%" stopColor="#e7038e" />
                  </linearGradient>
                </defs>
                <polygon points="8,8 72,8 72,23 28,57 72,57 72,72 8,72 8,57 52,23 8,23" fill="url(#zNavGrad)" />
              </svg>

              {/* Wordmark */}
              <span style={{
                fontWeight: 900,
                fontSize: isMobile ? 16 : 20,
                background: 'linear-gradient(135deg, #0d1b3e 0%, #1e40af 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.3px',
                whiteSpace: 'nowrap',
              }}>
                {cfg.navBrandText}
              </span>

              {/* Tagline pill */}
              {cfg.navTagline && !isMobile && (
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                  background: 'linear-gradient(135deg, #086afb, #e7038e)',
                  color: '#fff',
                  padding: '3px 10px', borderRadius: 20,
                  whiteSpace: 'nowrap', flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(8,106,251,0.25)',
                }}>
                  {cfg.navTagline}
                </span>
              )}
            </div>

            {/* ── Right: Nav actions ── */}
            <div style={{ display: 'flex', gap: isMobile ? 6 : 10, alignItems: 'center', flexShrink: 0 }}>

              {/* FAQ button */}
              <button
                onClick={() => navigate('/faq')}
                title="FAQ"
                aria-label="Open FAQ"
                style={{
                  background: 'none',
                  border: '1.5px solid currentColor',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  padding: '2px 8px',
                  lineHeight: 1.4,
                  color: '#555',
                  fontFamily: 'inherit',
                  flexShrink: 0,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#333'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#555'; }}
              >
                FAQ
              </button>

              {/* Login / outline button */}
              {cfg.navBtn1Visible && (
                <button
                  onClick={() => handleCtaClick(cfg.navBtn1Url)}
                  style={{
                    background: 'transparent',
                    border: '1.5px solid #cbd5e1',
                    borderRadius: 10,
                    padding: isMobile ? '7px 14px' : '9px 20px',
                    fontSize: isMobile ? 12 : 13,
                    fontWeight: 700,
                    color: '#334155',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#086afb'; e.currentTarget.style.color = '#086afb'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#334155'; }}
                >
                  {cfg.navBtn1Label}
                </button>
              )}

              {/* Register / primary CTA button */}
              {cfg.navBtn2Visible && (
                <button
                  onClick={() => handleCtaClick(cfg.navBtn2Url)}
                  style={{
                    background: 'linear-gradient(135deg, #086afb 0%, #e7038e 100%)',
                    border: 'none',
                    borderRadius: 10,
                    padding: isMobile ? '7px 14px' : '9px 22px',
                    fontSize: isMobile ? 12 : 13,
                    fontWeight: 700,
                    color: '#fff',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 3px 14px rgba(8,106,251,0.35)',
                    transition: 'opacity 0.15s, transform 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {cfg.navBtn2Label}
                </button>
              )}
              {cfg.navBtn3Visible && (
                <button
                  onClick={() => handleCtaClick(cfg.navBtn3Url)}
                  style={{
                    background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                    border: 'none',
                    borderRadius: 10,
                    padding: isMobile ? '7px 14px' : '9px 22px',
                    fontSize: isMobile ? 12 : 13,
                    fontWeight: 700,
                    color: '#fff',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 3px 14px rgba(13,148,136,0.35)',
                    transition: 'opacity 0.15s, transform 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {cfg.navBtn3Label}
                </button>
              )}
            </div>

          </div>
        </nav>
      )}

      {/* ── paid banner layout wrapper ────────────────────────────────────── */}
      <div style={{
        maxWidth: hasLeftBanners || hasRightBanners ? 1480 : 1200,
        margin: '0 auto',
        display: hasLeftBanners || hasRightBanners ? 'grid' : 'block',
        gridTemplateColumns: hasLeftBanners && hasRightBanners
          ? '200px 1fr 200px'
          : hasLeftBanners ? '200px 1fr' : hasRightBanners ? '1fr 200px' : undefined,
        gap: 20,
        alignItems: 'start',
        padding: isMobile ? '8px 0 0' : '24px 16px 0',
      }}>

        {/* Left banner column */}
        {hasLeftBanners && (
          <div style={{ paddingTop: 8 }}>
            {bLeft.map(b => <BannerCard key={b.id} b={b} isMobile={isMobile} />)}
          </div>
        )}

      <div
        style={{
          maxWidth: hasLeftBanners || hasRightBanners ? '100%' : 1200,
          margin: hasLeftBanners || hasRightBanners ? 0 : '0 auto',
          padding: isAdminMarketSignalsEmbed
            ? (isMobile ? '8px 0 32px' : '8px 0 40px')
            : (isMobile ? '20px 16px 48px' : '8px 8px 64px'),
          width: '100%',
          boxSizing: 'border-box',
        }}
      >

        {/* ── preview mode banner ───────────────────────────────────────── */}
        {isPreview && !snapshot?.published && (
          <div style={{
            background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
            padding: '10px 16px', marginBottom: 16,
            display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#92400e',
          }}>
            <span style={{ flexShrink: 0 }}>👁️</span>
            <span><strong>Preview mode</strong> — this page is unpublished. Publish it from the Admin Dashboard to make it live at <code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: 4 }}>/</code>.</span>
          </div>
        )}

        <>

        {/* ── About / Business Introduction (landing page only) ─────────── */}
        {isLandingContext && cfg.showAboutSection && (
          <div style={{
            background: cfg.maskAboutSection ? '#f1f5f9' : '#fff',
            border: '1px solid #e2e8f0', borderRadius: 16,
            padding: isMobile ? '24px 18px' : '36px 40px',
            marginBottom: isMobile ? 24 : 36,
            boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
          }}>
            {cfg.maskAboutSection ? (
              /* ── masked placeholder ── */
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ width: 180, height: 18, background: '#e2e8f0', borderRadius: 6, margin: '0 auto 14px', filter: 'blur(4px)' }} />
                <div style={{ width: '80%', height: 14, background: '#e2e8f0', borderRadius: 4, margin: '0 auto 8px', filter: 'blur(4px)' }} />
                <div style={{ width: '60%', height: 14, background: '#e2e8f0', borderRadius: 4, margin: '0 auto', filter: 'blur(4px)' }} />
              </div>
            ) : (
              <>
                {/* Badge + title + body */}
                {cfg.aboutBadge && (
                  <span style={{
                    display: 'inline-block', background: '#eff6ff', color: '#2563eb',
                    fontSize: 11, fontWeight: 700, padding: '3px 10px',
                    borderRadius: 20, marginBottom: 12, letterSpacing: 0.4,
                  }}>
                    {cfg.aboutBadge}
                  </span>
                )}
                <h2 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: '#1e293b', margin: '0 0 12px', lineHeight: 1.25 }}>
                  {cfg.aboutTitle}
                </h2>
                {cfg.aboutBody && (
                  <p style={{ fontSize: isMobile ? 14 : 15, color: '#64748b', lineHeight: 1.7, margin: '0 0 28px', whiteSpace: 'pre-wrap' }}>
                    {cfg.aboutBody}
                  </p>
                )}
                {/* Highlight cards */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: isMobile ? 12 : 16,
                }}>
                  {[
                    { icon: cfg.aboutHighlight1Icon, title: cfg.aboutHighlight1Title, text: cfg.aboutHighlight1Text },
                    { icon: cfg.aboutHighlight2Icon, title: cfg.aboutHighlight2Title, text: cfg.aboutHighlight2Text },
                    { icon: cfg.aboutHighlight3Icon, title: cfg.aboutHighlight3Title, text: cfg.aboutHighlight3Text },
                  ].filter(h => h.title || h.text).map((h, i) => (
                    <div key={i} style={{
                      background: '#f8fafc', borderRadius: 12,
                      padding: isMobile ? '14px 16px' : '18px 20px',
                      border: '1px solid #e2e8f0',
                    }}>
                      {h.icon && <span style={{ fontSize: 24, display: 'block', marginBottom: 8 }}>{h.icon}</span>}
                      <div style={{ fontWeight: 700, fontSize: isMobile ? 13 : 14, color: '#1e293b', marginBottom: 4 }}>{h.title}</div>
                      <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{h.text}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── announcement banner ───────────────────────────────────────── */}
        {oracleDisplay && !loading && (cfg.showPublicRefreshBanner ?? true) && (
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: 10, padding: '10px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1e40af',
            flexWrap: 'wrap',
          }}>
            <span>🔔</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              {signalDataRedacted ? (
                <>Public view shows layout only — figures below are not live data. Sign in with <strong>Market signals</strong> access to see real values.</>
              ) : (
                <>Signals refresh every 45s. Last updated: <strong>{lastRefresh.toLocaleTimeString()}</strong></>
              )}
            </span>
            <button onClick={load} style={{ background: '#dbeafe', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, color: '#1e40af', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Refresh
            </button>
          </div>
        )}

        {/* ── hero (public landing only — skip in admin Market Signals embed) ── */}
        {!isAdminMarketSignalsEmbed && (
        <div style={{
          position: 'relative',
          borderRadius: 20,
          overflow: 'hidden',
          color: '#fff',
          minHeight: isMobile ? 200 : 260,
          display: 'flex',
          alignItems: 'stretch',
          marginBottom: isMobile ? 20 : 32,
          backgroundImage: 'url(/assets/banners/banner-hero-1200x400.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
        }}>
          {/* Overlay: opaque dark-navy on left, fades to transparent on right */}
          <div style={{
            position: 'absolute', inset: 0,
            background: isMobile
              ? 'linear-gradient(to bottom, rgba(8,20,75,0.82) 0%, rgba(8,20,75,0.65) 50%, rgba(8,20,75,0.82) 100%)'
              : 'linear-gradient(to right, rgba(8,20,75,0.97) 0%, rgba(8,20,75,0.93) 40%, rgba(8,20,75,0.55) 68%, rgba(8,20,75,0.04) 100%)',
          }} />
          {/* Text content — left-aligned, sits in left ~58% */}
          <div style={{
            position: 'relative', zIndex: 1,
            width: isMobile ? '100%' : '60%',
            padding: isMobile ? '32px 20px' : '48px 52px',
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center',
            textAlign: isMobile ? 'center' : 'left',
          }}>
            <h1 style={{ fontSize: isMobile ? 24 : 38, fontWeight: 900, color: '#fff', margin: '0 0 14px', lineHeight: 1.2 }}>
              {cfg.heroTitle}
            </h1>
            <p style={{
              fontSize: isMobile ? 14 : 17, color: 'rgba(255,255,255,0.82)',
              margin: '0 0 28px', maxWidth: 500, lineHeight: 1.65,
              ...(isMobile ? { marginLeft: 'auto', marginRight: 'auto' } : {}),
            }}>
              {cfg.heroSubtitle}
            </p>
            {(cfg.showCtaPrimary || cfg.showCtaSecondary) && (
              <div style={{ display: 'flex', gap: 12, justifyContent: isMobile ? 'center' : 'flex-start', flexWrap: 'wrap' }}>
                {cfg.showCtaPrimary && (
                  <button
                    onClick={() => handleCtaClick(cfg.heroCtaPrimaryUrl)}
                    style={{
                      background: '#fff', color: branding.primaryColor,
                      border: 'none', borderRadius: 12,
                      padding: isMobile ? '12px 24px' : '14px 32px',
                      fontSize: isMobile ? 14 : 15, fontWeight: 700,
                      cursor: 'pointer', width: isMobile ? '100%' : 'auto',
                      transition: 'transform 0.15s, box-shadow 0.15s',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.25)'; }}
                  >
                    {cfg.heroCtaPrimary}
                  </button>
                )}
                {cfg.showCtaSecondary && (
                  <button
                    onClick={() => handleCtaClick(cfg.heroCtaSecondaryUrl)}
                    style={{
                      background: 'transparent', color: '#fff',
                      border: '2px solid rgba(255,255,255,0.6)',
                      borderRadius: 12,
                      padding: isMobile ? '12px 24px' : '14px 32px',
                      fontSize: isMobile ? 14 : 15, fontWeight: 600,
                      cursor: 'pointer', width: isMobile ? '100%' : 'auto',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    {cfg.heroCtaSecondary}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* ── Mobile banner strip — after hero, before signal data ─────── */}
        {isMobile && mobileBanners.length > 0 && (
          <div style={{ margin: isAdminMarketSignalsEmbed ? '0 0 16px' : '0 -16px 16px', maxWidth: '100%', overflow: 'hidden' }}>
            <div style={{ paddingLeft: isAdminMarketSignalsEmbed ? 0 : 16, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sponsored</span>
            </div>
            <MobileBannerStrip banners={mobileBanners} />
          </div>
        )}

        {/* ── error banner ──────────────────────────────────────────────── */}
        {error && !loading && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>🔄</span>
            <span style={{ flex: '1 1 160px', minWidth: 0 }}>Live signal data is refreshing — cached values shown below.</span>
            <button onClick={load} style={{ marginLeft: isMobile ? 0 : 'auto', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, color: '#64748b', cursor: 'pointer' }}>
              Retry
            </button>
          </div>
        )}

        {/* ── signal panels ───────────────────────────────────────────────── */}
        {/* BTC and Participation: rendered WITHOUT global blur when their individual masks are off */}
        {!loading && snapshot && (btcDisplay || microReversalDisplay || participationDisplay || aiRegimeDisplay || majorCoinsDisplay) && (
          <div>
            {(btcDisplay || microReversalDisplay || participationDisplay || aiRegimeDisplay) && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
                {aiRegimeDisplay && <AiRegimeBadge data={aiRegimeDisplay} masked={aiRegimeMasked} />}
                {btcDisplay && <BtcRegimeBadge regime={btcDisplay} masked={btcRegimeMasked} />}
                {microReversalDisplay && <MicroReversalBadge data={microReversalDisplay} />}
                {participationDisplay && <ParticipationBadge data={participationDisplay} masked={participationMasked} />}
              </div>
            )}
            {(aiRegimeDisplay || btcDisplay || participationDisplay) && (
              <p style={{ margin: '-8px 0 20px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                Tap <strong style={{ color: '#64748b' }}>?</strong> on any card to see all possible statuses and what they mean.
              </p>
            )}

            {majorCoinsDisplay && (
              <div style={{ marginBottom: 24 }}>
                <MajorCoinsMarketPanel
                  data={majorCoinsDisplay}
                  masked={majorCoinsMasked}
                  showChartStyleOptions={cfg.showMajorCoinsChartStyleOptions !== false}
                  defaultChartType={cfg.majorCoinsDefaultChart}
                />
              </div>
            )}

            {participationDisplay && participationDisplay.coins.length > 0 && (
              <CoinActivityBreakdownPanel
                participation={participationDisplay}
                sectorBreakdown={sectorBreakdownDisplay}
                futuresActivity={futuresActivityDisplay}
                showSectorTab={cfg.showSectorBreakdown === true}
                isMobile={isMobile}
                masked={participationMasked}
                enableCoinActions={enableCoinActions}
                onCoinClick={handleCoinClick}
              />
            )}
          </div>
        )}

        {/* Oracle Feed: still wrapped in global blur when maskAllSections is on */}
        {!loading && snapshot && signalsBlockedByMicroReversal && cfg.showOracleFeed && (
          <div style={{
            background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 12,
            padding: '14px 18px', marginBottom: 24, fontSize: 13, color: '#7f1d1d', lineHeight: 1.6,
          }}>
            <strong>Oracle signals paused.</strong> BTC is in a micro-reversal chop window (−0.10% to −0.50% drop from recent 5m high). Rankings will resume when the filter clears.
          </div>
        )}
        {!loading && snapshot && oracleDisplay && (
          <div style={signalDataRedacted ? REDACTED_SIGNAL_BLUR : {}}>
            <div style={{ marginBottom: 24 }}>
              <OracleFeedTable
                memeFeed={oracleDisplay}
                stockFeed={oracleStockDisplay}
                limit={oracleLimitCfg}
              />
            </div>
          </div>
        )}

        {/* ── features ──────────────────────────────────────────────────── */}
        <div style={{ marginBottom: isMobile ? 28 : 40 }}>
          <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#1e293b', marginBottom: 16 }}>
            How Our Signals Work
          </h2>
          <FeatureCards cfg={cfg} />
        </div>

        {/* ── proof sections ────────────────────────────────────────────── */}
        {(cfg.showProofRegime || cfg.showProofParticipation || cfg.showProofSignals || cfg.showProofWinningTrades) && (
          <div style={{ marginBottom: isMobile ? 28 : 40 }}>
            <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
              Proof It Works
            </h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.6 }}>
              Real tracked results — not back-tests. We log every signal and publish the outcomes.
            </p>
            {cfg.showProofRegime && <ProofRegimeSection cfg={cfg} isMobile={isMobile} redactSensitive={signalDataRedacted} />}
            {cfg.showProofParticipation && <ProofParticipationSection cfg={cfg} isMobile={isMobile} redactSensitive={signalDataRedacted} />}
            {cfg.showProofSignals && <ProofSignalsSection cfg={cfg} isMobile={isMobile} redactSensitive={signalDataRedacted} />}
            {cfg.showProofWinningTrades && (
              <ProofWinningTradesSection
                cfg={cfg}
                isMobile={isMobile}
                redactSensitive={winningTradesMasked}
                trades={winningTradesDisplay}
                currentPage={winningTradesPage}
                onPageChange={setWinningTradesPage}
                showStrategyName={cfg.showProofWinningTradesStrategyName}
                showDailyPnl={cfg.showProofWinningTradesDailyPnl}
                showDailyPnlPerStrategy={cfg.showProofWinningTradesDailyPnlPerStrategy}
                includeLosingTrades={cfg.showProofLosingTrades}
                displayLimit={cfg.winningTradesDisplayLimit}
              />
            )}
          </div>
        )}

        {/* ── bottom CTA banner — independently configurable ────────────── */}
        <div style={{
          position: 'relative',
          borderRadius: 20,
          overflow: 'hidden',
          color: '#fff',
          minHeight: isMobile ? 180 : 220,
          display: 'flex',
          alignItems: 'stretch',
          backgroundImage: 'url(/assets/banners/banner-hero-1200x400.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
        }}>
          {/* Strong overlay on left 70%, fading to transparent on the right so the image shines through */}
          <div style={{
            position: 'absolute', inset: 0,
            background: isMobile
              ? 'linear-gradient(to bottom, rgba(8,20,75,0.82) 0%, rgba(8,20,75,0.65) 50%, rgba(8,20,75,0.82) 100%)'
              : 'linear-gradient(to right, rgba(8,20,75,0.95) 0%, rgba(8,20,75,0.92) 45%, rgba(8,20,75,0.55) 72%, rgba(8,20,75,0.05) 100%)',
          }} />
          {/* Content: left-aligned text block, right side shows the image */}
          <div style={{
            position: 'relative', zIndex: 1,
            width: isMobile ? '100%' : '58%',
            padding: isMobile ? '32px 20px' : '44px 48px',
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center',
            textAlign: isMobile ? 'center' : 'left',
          }}>
          <h2 style={{ fontSize: isMobile ? 20 : 28, fontWeight: 800, margin: '0 0 10px', lineHeight: 1.3 }}>
            {cfg.ctaBannerTitle}
          </h2>
          <p style={{ fontSize: isMobile ? 13 : 15, opacity: 0.85, margin: '0 0 24px', maxWidth: 460, lineHeight: 1.65, ...(isMobile ? { marginLeft: 'auto', marginRight: 'auto' } : {}) }}>
            {cfg.ctaBannerSubtitle}
          </p>
          {(cfg.showCtaPrimary || cfg.showCtaSecondary) && (
            <div style={{ display: 'flex', gap: 12, justifyContent: isMobile ? 'center' : 'flex-start', flexWrap: 'wrap', padding: isMobile ? '0 4px' : 0 }}>
              {cfg.showCtaPrimary && (
                <button
                  onClick={() => handleCtaClick(cfg.heroCtaPrimaryUrl)}
                  style={{
                    background: '#fff', color: branding.primaryColor, border: 'none',
                    borderRadius: 12, padding: isMobile ? '12px 24px' : '14px 32px',
                    fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: 'pointer',
                    transition: 'transform 0.15s', width: isMobile ? '100%' : 'auto',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.03)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  {cfg.heroCtaPrimary}
                </button>
              )}
              {cfg.showCtaSecondary && (
                <button
                  onClick={() => handleCtaClick(cfg.heroCtaSecondaryUrl)}
                  style={{
                    background: 'transparent', color: '#fff',
                    border: '2px solid rgba(255,255,255,0.6)',
                    borderRadius: 12, padding: isMobile ? '12px 24px' : '14px 32px',
                    fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: 'pointer',
                    transition: 'border-color 0.15s', width: isMobile ? '100%' : 'auto',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = '#fff')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)')}
                >
                  {cfg.heroCtaSecondary}
                </button>
              )}
            </div>
          )}
          </div>
        </div>

        {/* ── footer ────────────────────────────────────────────────────── */}
        <div style={{
          marginTop: 40, borderTop: '1px solid #e2e8f0', paddingTop: 20,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row', gap: 12, textAlign: isMobile ? 'center' : 'left',
        }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{cfg.footerCopyright}</span>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-end' }}>
            {cfg.footerLink1Visible && (
              <button onClick={() => handleCtaClick(cfg.footerLink1Url)} style={{ background: 'none', border: 'none', fontSize: 13, color: '#64748b', cursor: 'pointer', padding: 0 }}>{cfg.footerLink1Label}</button>
            )}
            {cfg.footerLink2Visible && (
              <button onClick={() => handleCtaClick(cfg.footerLink2Url)} style={{ background: 'none', border: 'none', fontSize: 13, color: '#64748b', cursor: 'pointer', padding: 0 }}>{cfg.footerLink2Label}</button>
            )}
            {cfg.footerLink3Visible && (
              <button onClick={() => handleCtaClick(cfg.footerLink3Url)} style={{ background: 'none', border: 'none', fontSize: 13, color: '#64748b', cursor: 'pointer', padding: 0 }}>{cfg.footerLink3Label}</button>
            )}
            {cfg.footerLink4Visible && (
              <button onClick={() => handleCtaClick(cfg.footerLink4Url)} style={{ background: 'none', border: 'none', fontSize: 13, color: '#64748b', cursor: 'pointer', padding: 0 }}>{cfg.footerLink4Label}</button>
            )}
          </div>
        </div>
        </>

      </div>

        {/* Right banner column */}
        {hasRightBanners && (
          <div style={{ paddingTop: 8 }}>
            {bRight.map(b => <BannerCard key={b.id} b={b} isMobile={isMobile} />)}
          </div>
        )}

      </div>{/* end banner layout wrapper */}

      <CoinPositionsDrawer
        coin={selectedCoin}
        open={coinDrawerOpen}
        onClose={handleCloseCoinDrawer}
        isAdminContext={isAdminMarketSignalsEmbed || !!adminUser}
        canOpenPositions={canOpenPositions}
        onViewStrategy={handleViewStrategyFromDrawer}
      />
    </div>
  );
};

export default MarketLanding;
